import type { Music24WishTopic, WishSurface } from 'fym-shared/src/analytics/wishTopics';
import { createApi } from '@reduxjs/toolkit/query/react';
import { batch } from 'react-redux';
import qs from 'qs';
import type { CampaignLaunchResponse } from 'fym-shared/src/music24/campaignLaunch';
import type { TikTokCreatorInfoAnswer } from 'fym-shared/src/music24/tiktokPostSettings';
import type { YouTubePostingInfoAnswer } from 'fym-shared/src/music24/youtubePostSettings';
import type { PromoMediaInfoAnswer } from 'fym-shared/src/music24/aiVideoLabel';
import type {
  AssetPublishDefaultsResponse,
  AssetPublishListResponse,
  AssetPublishResponse,
  CreateAssetPublishBody,
  OneOffChannel,
  UpdateAssetPublishBody,
} from 'fym-shared/src/music24/oneOffPublish';
import type {
  CreateSongPublishBody,
  SongPublishDefaultsResponse,
  SongPublishListResponse,
  SongPublishResponse,
  UpdateSongPublishBody,
} from 'fym-shared/src/music24/songPublish';
import type {
  SpotifyCanvasSettingsResponse,
  SpotifyCanvasSongResponse,
  StartSpotifyCanvasBody,
  UpdateSpotifyCanvasSettingsBody,
} from 'fym-shared/src/music24/spotifyCanvas';
import type {
  ArtistBioDraftResponse,
  ArtistProfileResponse,
  ProfileMakeResponse,
  ProfileZipResponse,
  SpotifyBioUploadResponse,
  SpotifyProfileUploadResponse,
} from 'fym-shared/src/music24/artistProfile';
import type {
  ProfileSendOverviewResponse,
  ProfileSendResponse,
  ProfileSendSettingsResponse,
} from 'fym-shared/src/music24/profileSend';
import {
  SerializedAccountsListResponse,
  SerializedFile,
  SerializedSmartLinksListResponse,
} from 'fym-common/src/db/serializersTypes';
import type {
  StreamingDimensionResponse,
  StreamingEarningsResponse,
  StreamingOverviewResponse,
  StreamingTracksResponse,
} from 'fym-shared/src/music24/streaming';
import type {
  StreamingAcceleratorResponse,
  StreamingAudienceResponse,
  StreamingPlaySourcesResponse,
  StreamingShortVideoResponse,
} from 'fym-shared/src/music24/streamingSections';
import type { ArtistPortalStreamsByDimensionResponse } from 'fym-shared/src/smartlinks/artistPortals';
import type { PresaveFansReleasesPage, PresaveFansSummary } from 'fym-shared/src/smartlinks/presaveSubscriptions';
import type { CoverRedoResult, CoverRedoState } from 'fym-shared/src/music24/coverRedo';
import type { DateRedoResult, DateRedoState } from 'fym-shared/src/music24/dateRedo';
import { config } from '../../config/config';
import {
  AccountResponse,
  ArtistPortalServiceOptions,
  BeginArtistPortalAuthOptions,
  BeginArtistPortalAuthResponse,
  CompleteArtistPortalAuthOptions,
  CompleteArtistPortalAuthResponse,
  GetArtistPortalArtistsResponse,
  AddReleasedCatalogItemOptions,
  AddUnreleasedCatalogItemOptions,
  CatalogSongResponse,
  GetLyricTimingOptions,
  LyricTimingResponse,
  PatchLyricTimingOptions,
  ResyncLyricTimingOptions,
  MasterDownloadLinkResponse,
  MasterLinkOptions,
  MasterStreamLinkResponse,
  DismissCatalogReleaseEventOptions,
  GetCatalogReleaseEventsOptions,
  GetCatalogReleaseEventsResponse,
  GetCatalogSongOptions,
  GetClaimMatchOptions,
  GetClaimMatchResponse,
  CatalogClaimResponse,
  GetCatalogMergeCandidatesOptions,
  GetCatalogMergeCandidatesResponse,
  MergeCatalogItemsOptions,
  LinkCatalogItemOptions,
  MarkCatalogItemReleasedOptions,
  PatchCatalogSongOptions,
  CreateMasterUploadOptions,
  CreateMasterUploadResponse,
  CompleteMasterUploadOptions,
  AbortMasterUploadOptions,
  AbortPostVideoUploadOptions,
  CompletePostVideoUploadOptions,
  CompletePostVideoUploadResponse,
  CreatePostVideoUploadOptions,
  CreatePostVideoUploadResponse,
  CancelPresaveCampaignOptions,
  CancelPresaveCampaignResponse,
  CatalogItemResponse,
  ClaimCatalogItemOptions,
  CreateAccountOptions,
  CreatePresaveCampaignForSmartLinkOptions,
  CreatePresaveCampaignForSmartLinkResponse,
  CreateSmartLinkOptions,
  CreateTrackerOptions,
  CreateWidgetOptions,
  CreateWizardPlaylistLinkOptions,
  CreateWizardPresaveLinkOptions,
  GetBioLinkSettingsOptions,
  GetBioLinkSettingsResponse,
  UpdateBioLinkSettingsOptions,
  WizardSmartLinkResponse,
  CreateWizardReleaseLinkOptions,
  DeleteCatalogItemOptions,
  DeleteSmartLinkOptions,
  DeleteTrackerOptions,
  DeleteWidgetOptions,
  DismissCatalogSuggestionOptions,
  DuplicateSmartLinkOptions,
  FetchAccountStatsOptions,
  FetchSmartLinkStatsSummariesOptions,
  FetchSmartLinkStatsSummariesResponse,
  FetchSmartLinkThemesOptions,
  FetchSmartLinkThemesResponse,
  FetchAccountStatsResponse,
  FetchSmartLinkStatsOptions,
  FetchSmartLinkStatsResponse,
  GetAccountOptions,
  GetCatalogOptions,
  GetCatalogResponse,
  GetCatalogSuggestionsOptions,
  GetCatalogSuggestionsResponse,
  GetConnectionInsightsOptions,
  GetConnectionInsightsResponse,
  GetPresaveCampaignOptions,
  GetPresaveCampaignResponse,
  GetArtistPortalOverviewOptions,
  GetArtistPortalOverviewResponse,
  GetArtistPortalPlaylistsOptions,
  GetArtistPortalPlaylistsResponse,
  GetArtistPortalTrackDailyOptions,
  GetArtistPortalTrackDailyResponse,
  GetArtistPortalTracksOptions,
  GetArtistPortalTracksResponse,
  GetDittoStreamsByDimensionOptions,
  GetStreamingDimensionOptions,
  GetStreamingAudienceOptions,
  GetStreamingPeriodSectionOptions,
  GetStreamingShortVideoOptions,
  GetStreamingEarningsOptions,
  GetStreamingOverviewOptions,
  GetStreamingTracksOptions,
  GetServiceConnectionsOptions,
  GetServiceConnectionsResponse,
  GetSmartLinkOptions,
  GetSmartLinksOptions,
  GetTrackersResponse,
  GetUserOptions,
  GetUserResult,
  PatchCatalogItemOptions,
  PatchServiceConnectionOptions,
  PostImageOptions,
  PublishSmartLinkOptions,
  ReleasePresaveCampaignOptions,
  ReleasePresaveCampaignResponse,
  RemoveAccountDataOptions,
  RawGetServiceConnectionsResponse,
  RawServiceConnectionResponse,
  RequestEarlyAccessOptions,
  RequestServiceConnectionOptions,
  RemoveAccountDataResponse,
  SelectArtistPortalArtistOptions,
  ServiceConnectionResponse,
  SmartLinkResponse,
  TrackerResponse,
  UpdateAccountOptions,
  UpdatePresaveCampaignOptions,
  UpdatePresaveCampaignResponse,
  UnpublishSmartLinkOptions,
  UpdateSmartLinkOptions,
  UpdateTrackerOptions,
  UpdateWidgetOptions,
  VerifyAccountEmailOptions,
  VerifyAccountNameSlugOptions,
  VerifyEmailResult,
  VerifySlugResult,
  VerifySmartLinkSlugOptions,
  WidgetResponse,
  AddRolloutDraftsOptions,
  AddRolloutDraftsResponse,
  CreateKitAssetsOptions,
  CreateKitAssetsResult,
  CreateKitZipOptions,
  CreateKitZipResponse,
  GetKitZipOptions,
  GetKitZipResponse,
  GetCleanCopiesOptions,
  GetCleanCopiesResponse,
  KitProResOptions,
  KitProResResponse,
  GetKitAssetVersionsOptions,
  GetKitAssetVersionsResponse,
  AssetShareOptions,
  AssetShareResponse,
  AssetShareAccessResponse,
  PublicAssetShare,
  GetReleaseKitLookResponse,
  AccountTimeZoneOptions,
  AccountTimeZoneResponse,
  UpdateAccountTimeZoneOptions,
  GetReleaseKitOptions,
  GetReleaseKitResponse,
  GetReleaseKitsOptions,
  GetReleaseKitsResponse,
  WorkSummary,
  GetRolloutOptions,
  GetRolloutResponse,
  GetReleasesOptions,
  GetReleasesResponse,
  ReleaseOptions,
  ReleaseOverview,
  MarkReleaseViewedResponse,
  GetReleasePlanOptions,
  ReleasePlan,
  PlanStepOptions,
  SetPlanChannelsOptions,
  SetPlanStepChannelsOptions,
  SetPlanStepChannelsResponse,
  SkipPlanStepResponse,
  SwapPlanStepMediaOptions,
  ReelsPlanEnded,
  ReelsPlanMade,
  ReelsPlanOptions,
  ReelsPlanPreview,
  GetVideoCreditsOptions,
  GetVideoCreditsResponse,
  ReleaseKitLookOptions,
  RemakeKitAssetsOptions,
  RemakeKitAssetsPreview,
  CoverRedoOptions,
  StartCoverRedoOptions,
  DateRedoOptions,
  StartDateRedoOptions,
  UpdateKitLookOptions,
  UpdateKitLookResponse,
  VideoCredits,
} from './fymAPI.types';
import { toVideoCredits } from './utils/toVideoCredits';
import { hasNewFailure } from './utils/hasNewFailure';
import { addMadeKitAssets, addMadeReel } from './utils/madeIntoReleaseKit';
import {
  ConfirmPromoPostPostedOptions,
  DeletePromoPostOptions,
  GetPromoCalendarOptions,
  GetPromoCalendarResponse,
  GetPromoQuotaOptions,
  GetPromoQuotaResponse,
  GetTikTokCreatorInfoOptions,
  GetYouTubePostingInfoOptions,
  GetPromoMediaInfoOptions,
  MovePromoPostOptions,
  SchedulePromoPostOptions,
  SchedulePromoPostsOptions,
  SchedulePromoPostsResponse,
  LaunchPromoPostsOptions,
  CreatePromoCampaignOptions,
  CreatePromoCaptionsOptions,
  CreatePromoCaptionsResponse,
  CreatePromoPostsOptions,
  CreatePromoPostsResponse,
  DeletePromoChannelConnectionOptions,
  GetPromoCampaignsOptions,
  GetPromoCampaignsResponse,
  GetPromoChannelsOptions,
  GetPromoChannelsResponse,
  GetPromoChannelStatsOptions,
  GetPromoChannelStatsResponse,
  GetPromoConnectTargetsOptions,
  GetPromoConnectTargetsResponse,
  GetPromoPerformanceOptions,
  GetPromoPerformanceResponse,
  GetPromoPostsOptions,
  GetPromoPostsResponse,
  GetPromoSocialPostsOptions,
  GetPromoSocialPostsResponse,
  GetPromoSocialPostStatsOptions,
  GetPromoSocialPostStatsResponse,
  PatchPromoCampaignOptions,
  PatchPromoChannelOptions,
  PatchPromoPostOptions,
  PromoCampaignResponse,
  PromoChannelResponse,
  PromoPostResponse,
  SelectPromoConnectTargetOptions,
  StartPromoChannelConnectOptions,
  StartPromoChannelConnectResponse,
  ApproveReelOptions,
  ApproveReelResponse,
  CreateReelOptions,
  GetReelOptions,
  GetReelsOptions,
  GetReelsResponse,
  ReelResponse,
  ReelWithChildResponse,
  RegenerateReelOptions,
  RejectReelOptions,
} from '../../modules/music24/promote/types';
import {
  GetPromoSocialSummaryOptions,
  GetPromoSocialSummaryResponse,
} from '../../modules/music24/artistHome/SocialBlock/socialSummaryTypes';
import type {
  ApplyCoverDesignOptions,
  ApplyCoverDesignResponse,
  CoverDesignResponse,
  CreateCoverAiRunOptions,
  CreateCoverAiRunResponse,
  CreateCoverDesignOptions,
  DeleteCoverDesignOptions,
  GetCoverBackgroundsResponse,
  CreateCoverMakerLeadOptions,
  CreateCoverMakerLeadResponse,
  GetCoverMakerLeadResponse,
  GetCoverEditorOptions,
  GetCoverEditorResponse,
  UpdateCoverDesignOptions,
  GetFansSummaryOptions,
  GetFansReleasesOptions,
  ArtistProfileOptions,
  MakeProfileAssetsOptions,
  UseReleaseForProfileOptions,
  ProfileAssetOptions,
  ProfileAssetVersionsResponse,
  ProfileAssetResponse,
  MarkProfileAssetOptions,
  SaveArtistBioOptions,
  SaveArtistBioResponse,
  DraftArtistBioOptions,
  UploadBioToSpotifyOptions,
  UploadProfileToSpotifyOptions,
  ProfileSendOptions,
  StartProfileSendOptions,
  SaveProfileSendSettingsOptions,
} from './fymAPI.types';
import { customBaseQuery } from './utils/customBaseQuery';
import { withArtistPortalDefaults } from './utils/withArtistPortalDefaults';
import { setLicenseInfoResult } from '../slices/license/slice';
import { setAccountId, setUserToken } from '../slices/appInfo/slice';
import { store } from '../../store/Store';
import { APIType } from './types';
import { appInformation } from '../../utils/appInformation/appInformation';

export const baseUrl = config.fymApiURL(true);
console.log('Using redux fymAPI base url:', baseUrl);
const tagTypes = [
  'BioLinkSettings',
  'License',
  'User',
  'Account',
  'SmartLink',
  'SmartLinkForList',
  'AccountForList',
  'PresaveCampaign',
  'CatalogItem',
  'CatalogSuggestion',
  'ServiceConnection',
  'ConnectionInsight',
  'ArtistPortalConnection',
  'ArtistPortalOverview',
  'ArtistPortalTracks',
  'PromoChannels',
  'PromoCampaigns',
  'PromoPosts',
  'PromoCalendar',
  'PromoQuota',
  'PromoPerformance',
  'PromoSocialNumbers',
  'Reels',
  'ReleaseKit',
  'ReleaseKitLook',
  'AccountTimeZone',
  'VideoCredits',
  'Rollout',
  'ReelsPlanPreview',
  'KitZip',
  'KitProRes',
  'Work',
  'Streaming',
  'AssetShare',
  'AssetPublish',
  'SongPublish',
  'SpotifyCanvas',
  'Releases',
  'CoverEditor',
  'CoverBackgrounds',
  'FansSummary',
  'FansReleases',
  'CleanCopies',
  'ArtistProfile',
];

/** The tag of the release kit list (`getReleaseKits`). A song's kit uses its catalogItemUuid as id. */
export const RELEASE_KIT_LIST_ID = 'LIST';
/**
 * The tags that a change of the video credit balance makes stale: the balance, and the dry run of
 * the AI reels plan (`previewReelsPlan`), which names the credits left.
 */
export const VIDEO_CREDIT_TAGS = ['VideoCredits' as const, 'ReelsPlanPreview' as const];
/**
 * The tags that a change of the channels makes stale: the channels, and the dry run of the AI reels
 * plan, which says "no channel takes a reel".
 */
const CHANNEL_TAGS = ['PromoChannels' as const, 'ReelsPlanPreview' as const];
export const fymAPI = createApi({
  reducerPath: 'fymAPI',
  tagTypes,
  baseQuery: customBaseQuery(baseUrl, APIType.fym),
  endpoints: (builder) => ({
    getUser: builder.query<GetUserResult, GetUserOptions>({
      query: ({ userId }) => ({
        url: `users/${userId}`,
        method: 'GET',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Update license info and JWT token with fresh data
          dispatch(setLicenseInfoResult(data.license));
          await store.set('userToken', data.token);
          dispatch(setUserToken({ token: data.token }));
        } catch {}
      },
      providesTags: ['User', 'License'],
    }),
    sendFeedback: builder.mutation<void, string>({
      query: (feedback) => {
        const { version, platform, platformVersion, buildVersion } = appInformation;
        return {
          url: `common/feedback`,
          method: 'POST',
          body: JSON.stringify({
            feedback,
            appVersion: version,
            platform,
            platformVersion,
            buildVersion,
          }),
          headers: { 'content-type': 'application/json' },
        };
      },
    }),
    sendWish: builder.mutation<void, { topic: Music24WishTopic; surface: WishSurface; note: string }>({
      query: ({ topic, surface, note }) => {
        const { version, platform, platformVersion, buildVersion } = appInformation;
        return {
          url: `common/feedback`,
          method: 'POST',
          body: JSON.stringify({
            kind: 'wish',
            topic,
            surface,
            ...(note === '' ? {} : { feedback: note }),
            appVersion: version,
            platform,
            platformVersion,
            buildVersion,
          }),
          headers: { 'content-type': 'application/json' },
        };
      },
    }),
    getAccounts: builder.query<SerializedAccountsListResponse, void>({
      query: () => ({
        url: `accounts`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [...response.accounts.map(({ id }) => ({ type: 'AccountForList' as const, id })), 'AccountForList']
          : ['AccountForList'],
    }),
    getAccount: builder.query<AccountResponse, GetAccountOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}`,
        method: 'GET',
      }),
      providesTags: (response) => (response ? [{ type: 'Account', id: response.account.id }] : ['Account']),
    }),
    createAccount: builder.mutation<AccountResponse, CreateAccountOptions>({
      query: ({ name, slug }) => ({
        url: `accounts`,
        method: 'POST',
        body: JSON.stringify({ name, accountSlug: slug }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: () => ['License', 'User'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          batch(() => {
            dispatch(setAccountId({ accountId: data.account.id }));
            dispatch(
              fymAPI.util.updateQueryData('getAccount', { accountId: data.account.id }, (draft) => {
                Object.assign(draft, data);
              })
            );
            dispatch(
              fymAPI.util.updateQueryData('getAccounts', undefined, (draft) => {
                const existingAccountIndex = draft.accounts.findIndex((account) => account.id === data.account.id);
                if (existingAccountIndex === -1) {
                  draft.accounts.push(data.account);
                } else {
                  draft.accounts.splice(existingAccountIndex, 0, data.account);
                }
              })
            );
          });
        } catch {}
      },
    }),
    updateAccount: builder.mutation<AccountResponse, UpdateAccountOptions>({
      query: ({ id, name, slug, avatarFileId, platformDomain }) => ({
        url: `accounts/${id}`,
        method: 'PATCH',
        body: JSON.stringify({
          name,
          accountSlug: slug,
          avatarFileId: avatarFileId ?? null,
          platformDomain,
        }),
        headers: { 'content-type': 'application/json' },
      }),
      // The account's name, avatar and address are all drawn by its pages, and
      // its address is built into every page's urls. A cached SmartLink is
      // therefore stale the moment this succeeds.
      invalidatesTags: ['SmartLink'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getAccount', { accountId: data.account.id }, (draft) => {
              Object.assign(draft, data);
            })
          );
          dispatch(
            fymAPI.util.updateQueryData('getAccounts', undefined, (draft) => {
              const existingAccountIndex = draft.accounts.findIndex((account) => account.id === data.account.id);
              if (existingAccountIndex !== -1) {
                draft.accounts[existingAccountIndex] = data.account;
              }
            })
          );
        } catch {}
      },
    }),
    postFile: builder.mutation<SerializedFile, PostImageOptions>({
      query: ({ accountId, formData }) => ({
        url: `accounts/${accountId}/files`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: () => ['Account'],
    }),
    verifyAccountNameSlug: builder.mutation<VerifySlugResult, VerifyAccountNameSlugOptions>({
      query: (data) => ({
        url: `accountNames/verifySlug`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    verifyAccountEmail: builder.mutation<VerifyEmailResult, VerifyAccountEmailOptions>({
      query: (data) => ({
        url: `accounts/verifyEmail`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    getSmartLinks: builder.query<SerializedSmartLinksListResponse, GetSmartLinksOptions>({
      query: ({ accountId, page, search, limit, status }) => ({
        url: `accounts/${accountId}/smartLinks?${qs.stringify({ page, limit, search, status })}`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [...response.results.map(({ id }) => ({ type: 'SmartLinkForList' as const, id })), 'SmartLinkForList']
          : ['SmartLinkForList'],
    }),
    getSmartLink: builder.query<SmartLinkResponse, GetSmartLinkOptions>({
      query: ({ accountId, smartLinkId }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}`,
        method: 'GET',
      }),
      providesTags: (smartLink) =>
        smartLink ? ['SmartLink', { type: 'SmartLink' as const, id: smartLink.smartLink.id }] : ['SmartLink'],
    }),
    updateSmartLink: builder.mutation<SmartLinkResponse, UpdateSmartLinkOptions>({
      query: ({ accountId, smartLinkId, name, slug, backgroundFileId, theme, platformDomain }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}`,
        method: 'PATCH',
        body: JSON.stringify({ name, slug, backgroundFileId: backgroundFileId ?? null, theme, platformDomain }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    publishSmartLink: builder.mutation<SmartLinkResponse, PublishSmartLinkOptions>({
      query: ({ accountId, smartLinkId }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/publish`,
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList', 'CatalogItem'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    unpublishSmartLink: builder.mutation<SmartLinkResponse, UnpublishSmartLinkOptions>({
      query: ({ accountId, smartLinkId }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/unpublish`,
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'content-type': 'application/json' },
      }),
      // The same tags publishing invalidates: the index draws a Live pill from
      // the list, and the Catalog draws the page's status from its own rows.
      invalidatesTags: ['SmartLinkForList', 'CatalogItem'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    createSmartLink: builder.mutation<SmartLinkResponse, CreateSmartLinkOptions>({
      query: ({ accountId, name, slug, backgroundFileId, theme, platformDomain }) => ({
        url: `accounts/${accountId}/smartLinks`,
        method: 'POST',
        body: JSON.stringify({ name, slug, backgroundFileId, theme, platformDomain }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    deleteSmartLink: builder.mutation<void, DeleteSmartLinkOptions>({
      query: ({ accountId, smartLinkId }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SmartLinkForList', 'CatalogItem'],
    }),
    verifySmartLinkSlug: builder.mutation<VerifySlugResult, VerifySmartLinkSlugOptions>({
      query: ({ accountId, slug, smartLinkId }) => ({
        url: `accounts/${accountId}/smartLinks/verifySlug`,
        method: 'POST',
        body: JSON.stringify({ slug, smartLinkId }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    updateWidget: builder.mutation<WidgetResponse, UpdateWidgetOptions>({
      query: ({ accountId, smartLinkId, widgetUUID, position, type, data, hidden }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/widgets/${widgetUUID}`,
        method: 'PATCH',
        body: JSON.stringify({ position, type, data, hidden }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    createWidget: builder.mutation<WidgetResponse, CreateWidgetOptions>({
      query: ({ accountId, smartLinkId, type, data, position, hidden }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/widgets`,
        method: 'POST',
        body: JSON.stringify({ type, data, position, hidden }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    deleteWidget: builder.mutation<WidgetResponse, DeleteWidgetOptions>({
      query: ({ accountId, smartLinkId, widgetUUID }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/widgets/${widgetUUID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    createWizardReleaseLink: builder.mutation<WizardSmartLinkResponse, CreateWizardReleaseLinkOptions>({
      query: ({
        accountId,
        title,
        subtitle,
        extra,
        imageId,
        imageURL,
        urls,
        catalogTrackMaid,
        slug,
        theme,
        platformDomain,
        publish,
      }) => ({
        url: `accounts/${accountId}/wizards/releaseLink`,
        method: 'POST',
        body: JSON.stringify({
          title,
          subtitle,
          extra,
          imageId,
          imageURL,
          urls,
          catalogTrackMaid,
          slug,
          theme,
          platformDomain,
          publish,
        }),
        headers: { 'content-type': 'application/json' },
      }),
      // The catalog derives its smartlink column from these pages, so a page
      // built out of a catalog row leaves that row stale.
      invalidatesTags: ['SmartLinkForList', 'CatalogItem'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    createWizardPlaylistLink: builder.mutation<SmartLinkResponse, CreateWizardPlaylistLinkOptions>({
      query: ({ accountId, url }) => ({
        url: `accounts/${accountId}/wizards/playlistLink`,
        method: 'POST',
        body: JSON.stringify({ url }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    createWizardPresaveLink: builder.mutation<WizardSmartLinkResponse, CreateWizardPresaveLinkOptions>({
      query: ({
        accountId,
        title,
        artistName,
        releaseDate,
        imageId,
        imageURL,
        catalogTrackMaid,
        catalogItemUuid,
        services,
        slug,
        theme,
        platformDomain,
        publish,
      }) => ({
        url: `accounts/${accountId}/wizards/presaveLink`,
        method: 'POST',
        body: JSON.stringify({
          title,
          artistName,
          releaseDate,
          imageId,
          imageURL,
          catalogTrackMaid,
          catalogItemUuid,
          services,
          slug,
          theme,
          platformDomain,
          publish,
        }),
        headers: { 'content-type': 'application/json' },
      }),
      // The catalog derives its smartlink column from these pages, so a page
      // built out of a catalog row leaves that row stale.
      invalidatesTags: ['SmartLinkForList', 'CatalogItem'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    getPresaveCampaign: builder.query<GetPresaveCampaignResponse, GetPresaveCampaignOptions>({
      // `campaignId` is the uuid the pre-save block carries: a loaded SmartLink
      // never holds the numeric id. The read route takes either.
      query: ({ accountId, campaignId }) => ({
        url: `accounts/${accountId}/presaveCampaigns/${campaignId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { campaignId }) => [{ type: 'PresaveCampaign' as const, id: `${campaignId}` }],
    }),
    createPresaveCampaignForSmartLink: builder.mutation<
      CreatePresaveCampaignForSmartLinkResponse,
      CreatePresaveCampaignForSmartLinkOptions
    >({
      query: ({ accountId, smartLinkId, title, artistName, releaseDate }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/presaveCampaign`,
        method: 'POST',
        body: JSON.stringify({ title, artistName, releaseDate }),
        headers: { 'content-type': 'application/json' },
      }),
      // The campaign is written, the block is not: the widget POST that follows
      // invalidates the SmartLink. Only the campaign's own cache entries are
      // listed here, under both keys the read route accepts.
      invalidatesTags: (result) =>
        result === undefined
          ? []
          : [
              { type: 'PresaveCampaign' as const, id: `${result.campaign.id}` },
              { type: 'PresaveCampaign' as const, id: result.campaign.uuid },
            ],
    }),
    updatePresaveCampaign: builder.mutation<UpdatePresaveCampaignResponse, UpdatePresaveCampaignOptions>({
      query: ({ accountId, campaignId, title, artistName, releaseDate }) => ({
        url: `accounts/${accountId}/presaveCampaigns/${campaignId}`,
        method: 'PATCH',
        body: JSON.stringify({ title, artistName, releaseDate }),
        headers: { 'content-type': 'application/json' },
      }),
      // The public block draws the campaign's title and counts down to its
      // date, so the page goes stale with the campaign. Both campaign keys are
      // listed for the same reason as the release: the editor reads by uuid,
      // this call goes by numeric id.
      invalidatesTags: (result, error, { campaignId, campaignUuid, smartLinkId }) => [
        { type: 'PresaveCampaign' as const, id: `${campaignId}` },
        ...(campaignUuid === undefined ? [] : [{ type: 'PresaveCampaign' as const, id: campaignUuid }]),
        ...(smartLinkId === undefined ? [] : [{ type: 'SmartLink' as const, id: smartLinkId }]),
        'SmartLinkForList' as const,
      ],
    }),
    releasePresaveCampaign: builder.mutation<ReleasePresaveCampaignResponse, ReleasePresaveCampaignOptions>({
      query: ({ accountId, campaignId, urls }) => ({
        url: `accounts/${accountId}/presaveCampaigns/${campaignId}/release`,
        method: 'POST',
        body: JSON.stringify({ urls }),
        headers: { 'content-type': 'application/json' },
      }),
      // The release rewrites the campaign's status and appends the service
      // blocks to the page, so both the panel and the SmartLink go stale. Both
      // cache keys are listed: the panel reads the campaign by uuid, the
      // release goes by numeric id.
      invalidatesTags: (result, error, { campaignId, campaignUuid, smartLinkId }) => [
        { type: 'PresaveCampaign' as const, id: `${campaignId}` },
        ...(campaignUuid === undefined ? [] : [{ type: 'PresaveCampaign' as const, id: campaignUuid }]),
        ...(smartLinkId === undefined ? [] : [{ type: 'SmartLink' as const, id: smartLinkId }]),
        'SmartLinkForList' as const,
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, { smartLink: data.smartLink });
              }
            )
          );
        } catch {}
      },
    }),
    cancelPresaveCampaign: builder.mutation<CancelPresaveCampaignResponse, CancelPresaveCampaignOptions>({
      query: ({ accountId, campaignId }) => ({
        url: `accounts/${accountId}/presaveCampaigns/${campaignId}/cancel`,
        method: 'POST',
      }),
      // Same two cache keys as the release, for the same reason: the panel reads
      // the campaign by uuid, the cancel goes by numeric id. The SmartLink is
      // listed too — the block serialises as closed once the campaign is
      // cancelled, so the preview is stale until it is refetched.
      invalidatesTags: (result, error, { campaignId, campaignUuid, smartLinkId }) => [
        { type: 'PresaveCampaign' as const, id: `${campaignId}` },
        ...(campaignUuid === undefined ? [] : [{ type: 'PresaveCampaign' as const, id: campaignUuid }]),
        ...(smartLinkId === undefined ? [] : [{ type: 'SmartLink' as const, id: smartLinkId }]),
        'SmartLinkForList' as const,
      ],
    }),
    getCatalog: builder.query<GetCatalogResponse, GetCatalogOptions>({
      // `qs.stringify` drops the undefined keys, so "all releases, no search"
      // sends a bare url and shares no cache entry with a filtered read.
      query: ({ accountId, search, filter }) => ({
        url: `accounts/${accountId}/catalog?${qs.stringify({ search, filter })}`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [...response.items.map(({ uuid }) => ({ type: 'CatalogItem' as const, id: uuid })), 'CatalogItem']
          : ['CatalogItem'],
    }),
    claimCatalogItem: builder.mutation<CatalogClaimResponse, ClaimCatalogItemOptions>({
      query: ({
        accountId,
        catalogTrackMaid,
        ownership,
        title,
        artistName,
        releaseType,
        releaseDate,
        artworkUrl,
        serviceLinks,
        linkToItemUuid,
        keepSeparate,
        mergeIntoItemUuid,
        addToBioLink,
      }) => ({
        url: `accounts/${accountId}/catalog/claim`,
        method: 'POST',
        body: JSON.stringify({
          catalogTrackMaid,
          ownership,
          title,
          artistName,
          releaseType,
          releaseDate,
          artworkUrl,
          serviceLinks,
          linkToItemUuid,
          keepSeparate,
          mergeIntoItemUuid,
          addToBioLink,
        }),
        headers: { 'content-type': 'application/json' },
      }),
      // The suggestions are recomputed from the catalog on every read, so a
      // claim takes the track off that panel as well as adding a row.
      invalidatesTags: ['CatalogItem', 'CatalogSuggestion', 'SmartLink', 'SmartLinkForList'],
    }),
    addReleasedCatalogItem: builder.mutation<CatalogItemResponse, AddReleasedCatalogItemOptions>({
      query: ({
        accountId,
        catalogTrackMaid,
        ownership,
        title,
        artistName,
        releaseType,
        releaseDate,
        artworkUrl,
        artworkFileId,
        serviceLinks,
        isrc,
        upc,
        addToBioLink,
      }) => ({
        url: `accounts/${accountId}/catalog/released`,
        method: 'POST',
        body: JSON.stringify({
          catalogTrackMaid,
          ownership,
          title,
          artistName,
          releaseType,
          releaseDate,
          artworkUrl,
          artworkFileId,
          serviceLinks,
          isrc,
          upc,
          addToBioLink,
        }),
        headers: { 'content-type': 'application/json' },
      }),
      // A release the artist added by hand can be the one a suggestion was
      // offering, so the panel is stale too.
      invalidatesTags: ['CatalogItem', 'CatalogSuggestion', 'SmartLink', 'SmartLinkForList'],
    }),
    addUnreleasedCatalogItem: builder.mutation<CatalogItemResponse, AddUnreleasedCatalogItemOptions>({
      query: ({ accountId, title, artistName, releaseType, releaseDate, artworkFileId, addToBioLink }) => ({
        url: `accounts/${accountId}/catalog/unreleased`,
        method: 'POST',
        body: JSON.stringify({ title, artistName, releaseType, releaseDate, artworkFileId, addToBioLink }),
        headers: { 'content-type': 'application/json' },
      }),
      // The Bio Link auto add changes the Bio Link page too (design smartlink-editor e1).
      invalidatesTags: ['CatalogItem', 'SmartLink', 'SmartLinkForList'],
    }),
    getBioLinkSettings: builder.query<GetBioLinkSettingsResponse, GetBioLinkSettingsOptions>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/bio-link-settings`, method: 'GET' }),
      providesTags: ['BioLinkSettings'],
    }),
    updateBioLinkSettings: builder.mutation<GetBioLinkSettingsResponse, UpdateBioLinkSettingsOptions>({
      query: ({ accountId, autoAddSongs, position, keepSongs }) => ({
        url: `accounts/${accountId}/bio-link-settings`,
        method: 'PUT',
        body: JSON.stringify({ autoAddSongs, position, keepSongs }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['BioLinkSettings'],
    }),
    patchCatalogItem: builder.mutation<CatalogItemResponse, PatchCatalogItemOptions>({
      query: ({
        accountId,
        itemUuid,
        ownership,
        title,
        artistName,
        releaseType,
        releaseDate,
        artworkFileId,
        serviceLinks,
      }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}`,
        method: 'PATCH',
        body: JSON.stringify({ ownership, title, artistName, releaseType, releaseDate, artworkFileId, serviceLinks }),
        headers: { 'content-type': 'application/json' },
      }),
      // A new cover gives the kit of the song new colours (the server stores them before it answers).
      invalidatesTags: (result, error, { itemUuid, artworkFileId }) => [
        { type: 'CatalogItem' as const, id: itemUuid },
        'CatalogItem',
        ...(artworkFileId === undefined
          ? []
          : [
              { type: 'ReleaseKit' as const, id: itemUuid },
              { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
            ]),
      ],
    }),
    deleteCatalogItem: builder.mutation<void, DeleteCatalogItemOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}`,
        method: 'DELETE',
      }),
      // Un-claiming puts the track back among the candidates, so the
      // suggestions panel is stale too.
      invalidatesTags: ['CatalogItem', 'CatalogSuggestion'],
    }),
    getCatalogSong: builder.query<CatalogSongResponse, GetCatalogSongOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/song`,
        method: 'GET',
      }),
      providesTags: (result, error, { itemUuid }) => [{ type: 'CatalogItem' as const, id: itemUuid }],
    }),
    patchCatalogSong: builder.mutation<CatalogSongResponse, PatchCatalogSongOptions>({
      query: ({ accountId, itemUuid, lyrics, isrc, upc }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/song`,
        method: 'PATCH',
        body: JSON.stringify({ lyrics, isrc, upc }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { itemUuid }) => [{ type: 'CatalogItem' as const, id: itemUuid }, 'CatalogItem'],
    }),
    // The lyric timing (spec 2026-10-05 §5.9). It shares the tag of the song, so the realtime event
    // `catalog.song.updated` (sync started, synced, failed, an edit of another member) reads it again.
    getLyricTiming: builder.query<LyricTimingResponse, GetLyricTimingOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/lyric-timing`,
        method: 'GET',
      }),
      providesTags: (result, error, { itemUuid }) => [{ type: 'CatalogItem' as const, id: itemUuid }],
    }),
    // No tag goes stale: the editor writes the answer into the cache itself (it keeps its optimistic
    // state), and the event of the write refreshes the other members.
    patchLyricTiming: builder.mutation<LyricTimingResponse, PatchLyricTimingOptions>({
      query: ({ accountId, itemUuid, revision, lines }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/lyric-timing`,
        method: 'PATCH',
        body: JSON.stringify({ revision, lines }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    resyncLyricTiming: builder.mutation<LyricTimingResponse, ResyncLyricTimingOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/lyric-timing/resync`,
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { itemUuid }) => [{ type: 'CatalogItem' as const, id: itemUuid }, 'CatalogItem'],
    }),
    // The master goes straight to S3 in parts (D29): Cloudflare refuses a body over 100 MB.
    // The master player (design release-kit o): POST, so a read-only token cannot make a link.
    createMasterStreamLink: builder.mutation<MasterStreamLinkResponse, MasterLinkOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/master/stream`,
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    createMasterDownloadLink: builder.mutation<MasterDownloadLinkResponse, MasterLinkOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/master/download`,
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    createMasterUpload: builder.mutation<CreateMasterUploadResponse, CreateMasterUploadOptions>({
      query: ({ accountId, itemUuid, fileName, contentType, sizeBytes, rightsConfirmed }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/master/uploads`,
        method: 'POST',
        body: JSON.stringify({ fileName, contentType, sizeBytes, rightsConfirmed }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    completeMasterUpload: builder.mutation<CatalogSongResponse, CompleteMasterUploadOptions>({
      query: ({ accountId, itemUuid, uploadId, key, parts }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/master/uploads/${encodeURIComponent(uploadId)}/complete`,
        method: 'POST',
        body: JSON.stringify({ key, parts }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { itemUuid }) => [
        { type: 'CatalogItem' as const, id: itemUuid },
        'CatalogItem',
        'Account',
      ],
    }),
    abortMasterUpload: builder.mutation<void, AbortMasterUploadOptions>({
      query: ({ accountId, itemUuid, uploadId, key }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/master/uploads/${encodeURIComponent(uploadId)}?${qs.stringify({ key })}`,
        method: 'DELETE',
      }),
    }),
    // The Composer video upload: a presigned multipart upload straight to S3, like the master.
    createPostVideoUpload: builder.mutation<CreatePostVideoUploadResponse, CreatePostVideoUploadOptions>({
      query: ({ accountId, fileName, contentType, sizeBytes }) => ({
        url: `accounts/${accountId}/promo/post-media/uploads`,
        method: 'POST',
        body: JSON.stringify({ fileName, contentType, sizeBytes }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    completePostVideoUpload: builder.mutation<CompletePostVideoUploadResponse, CompletePostVideoUploadOptions>({
      query: ({ accountId, uploadId, key, parts, measures }) => ({
        url: `accounts/${accountId}/promo/post-media/uploads/${encodeURIComponent(uploadId)}/complete`,
        method: 'POST',
        body: JSON.stringify({ key, parts, measures }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    abortPostVideoUpload: builder.mutation<void, AbortPostVideoUploadOptions>({
      query: ({ accountId, uploadId, key }) => ({
        url: `accounts/${accountId}/promo/post-media/uploads/${encodeURIComponent(uploadId)}?${qs.stringify({ key })}`,
        method: 'DELETE',
      }),
    }),
    getCatalogReleaseEvents: builder.query<GetCatalogReleaseEventsResponse, GetCatalogReleaseEventsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/catalog/release-events`,
        method: 'GET',
      }),
      providesTags: ['CatalogItem'],
    }),
    dismissCatalogReleaseEvent: builder.mutation<void, DismissCatalogReleaseEventOptions>({
      query: ({ accountId, eventId }) => ({
        url: `accounts/${accountId}/catalog/release-events/${eventId}/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: ['CatalogItem'],
    }),
    // A pasted link makes a typed draft a released song (design catalog-add-song c4). The row keeps
    // its title, cover, master and lyrics; the suggestions can change too.
    markCatalogItemReleased: builder.mutation<CatalogItemResponse, MarkCatalogItemReleasedOptions>({
      query: ({ accountId, itemUuid, serviceLinks, releaseDate, releaseType, artworkUrl, isrc, upc }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/released`,
        method: 'POST',
        body: JSON.stringify({ serviceLinks, releaseDate, releaseType, artworkUrl, isrc, upc }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { itemUuid }) => [
        { type: 'CatalogItem' as const, id: itemUuid },
        'CatalogItem',
        'CatalogSuggestion',
      ],
    }),
    // A link promotes the unreleased row to the released track: the row, the
    // suggestions and the release cards all change.
    linkCatalogItem: builder.mutation<void, LinkCatalogItemOptions>({
      query: ({ accountId, itemUuid, maid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/link`,
        method: 'POST',
        body: JSON.stringify({ maid }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['CatalogItem', 'CatalogSuggestion'],
    }),
    // design catalog-merge a2: the rows one row can merge with, the likely duplicates first.
    getCatalogMergeCandidates: builder.query<GetCatalogMergeCandidatesResponse, GetCatalogMergeCandidatesOptions>({
      query: ({ accountId, itemUuid }) => ({
        url: `accounts/${accountId}/catalog/${itemUuid}/merge-candidates`,
        method: 'GET',
      }),
      providesTags: ['CatalogItem'],
    }),
    // design catalog-merge a3: `keepUuid` stays, `fromItemUuid` leaves the catalog.
    mergeCatalogItems: builder.mutation<CatalogItemResponse, MergeCatalogItemsOptions>({
      query: ({ accountId, keepUuid, fromItemUuid }) => ({
        url: `accounts/${accountId}/catalog/${keepUuid}/merge`,
        method: 'POST',
        body: JSON.stringify({ fromItemUuid }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['CatalogItem', 'CatalogSuggestion'],
    }),
    getClaimMatch: builder.query<GetClaimMatchResponse, GetClaimMatchOptions>({
      query: ({ accountId, maid }) => ({
        url: `accounts/${accountId}/catalog/claim-match?${qs.stringify({ maid })}`,
        method: 'GET',
      }),
      providesTags: ['CatalogItem'],
    }),
    getCatalogSuggestions: builder.query<GetCatalogSuggestionsResponse, GetCatalogSuggestionsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/catalog/suggestions`,
        method: 'GET',
      }),
      providesTags: ['CatalogSuggestion'],
    }),
    dismissCatalogSuggestion: builder.mutation<void, DismissCatalogSuggestionOptions>({
      query: ({ accountId, catalogTrackMaid, notTheSame }) => ({
        url: `accounts/${accountId}/catalog/suggestions/dismiss`,
        method: 'POST',
        body: JSON.stringify({ catalogTrackMaid, notTheSame }),
        headers: { 'content-type': 'application/json' },
      }),
      // The row leaves at the tap rather than after the round trip: "not mine"
      // is an answer about the artist's own work, so it cannot be wrong, and
      // the call is idempotent if it has to be repeated.
      onQueryStarted({ accountId, catalogTrackMaid, notTheSame }, { dispatch }) {
        dispatch(
          fymAPI.util.updateQueryData('getCatalogSuggestions', { accountId }, (draft) => {
            const index = draft.suggestions.findIndex((suggestion) => suggestion.catalogTrackMaid === catalogTrackMaid);
            const found = draft.suggestions[index];
            if (found === undefined) {
              return;
            }
            if (notTheSame === true) {
              // design catalog-merge c1: the row stays as a plain new release.
              found.matchesCatalogItem = null;
            } else {
              draft.suggestions.splice(index, 1);
            }
          })
        );
      },
    }),
    getServiceConnections: builder.query<GetServiceConnectionsResponse, GetServiceConnectionsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/serviceConnections`,
        method: 'GET',
      }),
      transformResponse: (response: RawGetServiceConnectionsResponse): GetServiceConnectionsResponse => ({
        connections: response.connections.map(withArtistPortalDefaults),
      }),
      providesTags: ['ServiceConnection', 'ArtistPortalConnection'],
    }),
    getConnectionInsights: builder.query<GetConnectionInsightsResponse, GetConnectionInsightsOptions>({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/catalog/connections/insights${qs.stringify({ days }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
      providesTags: ['ConnectionInsight'],
    }),
    patchServiceConnection: builder.mutation<ServiceConnectionResponse, PatchServiceConnectionOptions>({
      query: ({ accountId, integrationType, artistProfileUrl }) => ({
        url: `accounts/${accountId}/serviceConnections/${integrationType}`,
        method: 'PATCH',
        body: JSON.stringify({ artistProfileUrl }),
        headers: { 'content-type': 'application/json' },
      }),
      transformResponse: (response: RawServiceConnectionResponse): ServiceConnectionResponse => ({
        connection: withArtistPortalDefaults(response.connection),
      }),
      // A saved url registers a daily read of that profile, and the first read
      // runs at once — so both the follower line and the releases it suggests
      // are stale the moment the url changes.
      invalidatesTags: ['ServiceConnection', 'ConnectionInsight', 'CatalogSuggestion'],
    }),
    requestServiceConnection: builder.mutation<ServiceConnectionResponse, RequestServiceConnectionOptions>({
      query: ({ accountId, integrationType, reason }) => ({
        url: `accounts/${accountId}/serviceConnections/${integrationType}/request`,
        method: 'POST',
        body: JSON.stringify(reason === undefined ? {} : { reason }),
        headers: { 'content-type': 'application/json' },
      }),
      transformResponse: (response: RawServiceConnectionResponse): ServiceConnectionResponse => ({
        connection: withArtistPortalDefaults(response.connection),
      }),
      invalidatesTags: ['ServiceConnection'],
    }),
    /*
     * The early-access waiting list for a service we have not built yet. The
     * body carries the optional login email and nothing else. Leaving the list
     * is `disconnectArtistPortal` (the same DELETE).
     */
    requestEarlyAccess: builder.mutation<ServiceConnectionResponse, RequestEarlyAccessOptions>({
      query: ({ accountId, integrationType, loginEmail }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}/earlyAccess/request`,
        method: 'POST',
        body: JSON.stringify(loginEmail === undefined ? {} : { loginEmail }),
        headers: { 'content-type': 'application/json' },
      }),
      transformResponse: (response: RawServiceConnectionResponse): ServiceConnectionResponse => ({
        connection: withArtistPortalDefaults(response.connection),
      }),
      invalidatesTags: ['ServiceConnection'],
    }),
    /*
     * Artist portals. The four reads answer from the tables music24-api fills
     * every night; the five writes all change the connection row, so each of
     * them drops the cards and the numbers together.
     */
    getArtistPortalOverview: builder.query<GetArtistPortalOverviewResponse, GetArtistPortalOverviewOptions>({
      query: ({ accountId, days, from, to }) => ({
        url: `accounts/${accountId}/artistPortals/overview?${qs.stringify({ days, from, to })}`,
        method: 'GET',
      }),
      providesTags: ['ArtistPortalOverview'],
    }),
    getArtistPortalTracks: builder.query<GetArtistPortalTracksResponse, GetArtistPortalTracksOptions>({
      query: ({ accountId, integrationType, sort, limit }) => ({
        url: `accounts/${accountId}/artistPortals/tracks?${qs.stringify({ integrationType, sort, limit })}`,
        method: 'GET',
      }),
      providesTags: ['ArtistPortalTracks'],
    }),
    getArtistPortalTrackDaily: builder.query<GetArtistPortalTrackDailyResponse, GetArtistPortalTrackDailyOptions>({
      query: ({ accountId, integrationType, externalId, days, from, to }) => ({
        url: `accounts/${accountId}/artistPortals/tracks/${encodeURIComponent(integrationType)}/${encodeURIComponent(externalId)}/daily?${qs.stringify(
          {
            days,
            from,
            to,
          }
        )}`,
        method: 'GET',
      }),
      providesTags: ['ArtistPortalTracks'],
    }),
    getArtistPortalPlaylists: builder.query<GetArtistPortalPlaylistsResponse, GetArtistPortalPlaylistsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/artistPortals/playlists`,
        method: 'GET',
      }),
      providesTags: ['ArtistPortalOverview'],
    }),
    /*
     * Streaming (design your-intelligence). The server cuts every period to the plan history
     * (`historyDaysLimit`) and hides every amount from a member who may not see earnings.
     */
    getStreamingOverview: builder.query<StreamingOverviewResponse, GetStreamingOverviewOptions>({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/streaming/overview?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    getStreamingTracks: builder.query<StreamingTracksResponse, GetStreamingTracksOptions>({
      query: ({ accountId, days, limit }) => ({
        url: `accounts/${accountId}/streaming/tracks?${qs.stringify({ days, limit })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    getDittoStreamsByDimension: builder.query<
      ArtistPortalStreamsByDimensionResponse,
      GetDittoStreamsByDimensionOptions
    >({
      query: ({ accountId, dimension, days, trackExternalId }) => ({
        url: `accounts/${accountId}/artistPortals/ditto/streams/${dimension === 'store' ? 'byStore' : 'byCountry'}?${qs.stringify(
          { days, trackExternalId }
        )}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    /*
     * Stores, Countries and Earnings from every connected source (owner decision 2026-10-06). A
     * distributor wins each store and country that it reports, so nothing is counted twice.
     */
    getStreamingDimension: builder.query<StreamingDimensionResponse, GetStreamingDimensionOptions>({
      query: ({ accountId, dimension, days }) => ({
        url: `accounts/${accountId}/streaming/${dimension === 'store' ? 'stores' : 'countries'}?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    /*
     * The private period numbers (design streaming-sections a to d): one source at a time, by month.
     */
    getStreamingAudience: builder.query<StreamingAudienceResponse, GetStreamingAudienceOptions>({
      query: ({ accountId, days, source, month }) => ({
        url: `accounts/${accountId}/streaming/audience?${qs.stringify({ days, source, month })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    getStreamingPlaySources: builder.query<StreamingPlaySourcesResponse, GetStreamingPeriodSectionOptions>({
      query: ({ accountId, days, source }) => ({
        url: `accounts/${accountId}/streaming/playSources?${qs.stringify({ days, source })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    getStreamingShortVideo: builder.query<StreamingShortVideoResponse, GetStreamingShortVideoOptions>({
      query: ({ accountId, days, source, platform }) => ({
        url: `accounts/${accountId}/streaming/shortVideo?${qs.stringify({ days, source, platform })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    getStreamingAccelerator: builder.query<StreamingAcceleratorResponse, GetStreamingPeriodSectionOptions>({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/streaming/accelerator?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    getStreamingEarnings: builder.query<StreamingEarningsResponse, GetStreamingEarningsOptions>({
      query: ({ accountId, groupBy, from, to }) => ({
        url: `accounts/${accountId}/streaming/earnings?${qs.stringify({ groupBy, from, to })}`,
        method: 'GET',
      }),
      providesTags: ['Streaming'],
    }),
    beginArtistPortalAuth: builder.mutation<BeginArtistPortalAuthResponse, BeginArtistPortalAuthOptions>({
      query: ({ accountId, integrationType, returnTo }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}/auth/begin`,
        method: 'POST',
        ...(returnTo === undefined
          ? {}
          : { body: JSON.stringify({ returnTo }), headers: { 'content-type': 'application/json' } }),
      }),
    }),
    /** A mutation, not a query: every call asks the provider, and nothing may cache the answer. */
    getArtistPortalArtists: builder.mutation<GetArtistPortalArtistsResponse, ArtistPortalServiceOptions>({
      query: ({ accountId, integrationType }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}/artists`,
        method: 'GET',
      }),
    }),
    completeArtistPortalAuth: builder.mutation<CompleteArtistPortalAuthResponse, CompleteArtistPortalAuthOptions>({
      query: ({ accountId, integrationType, body }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}/auth/complete`,
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      transformResponse: (response: {
        connection: RawServiceConnectionResponse['connection'];
        artists: CompleteArtistPortalAuthResponse['artists'];
        returnTo?: string;
      }): CompleteArtistPortalAuthResponse => ({
        connection: withArtistPortalDefaults(response.connection),
        artists: response.artists,
        ...(response.returnTo === undefined ? {} : { returnTo: response.returnTo }),
      }),
      invalidatesTags: ['ServiceConnection', 'ArtistPortalConnection', 'ArtistPortalOverview', 'ArtistPortalTracks'],
    }),
    selectArtistPortalArtist: builder.mutation<ServiceConnectionResponse, SelectArtistPortalArtistOptions>({
      query: ({ accountId, integrationType, externalArtistId }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}/artist`,
        method: 'POST',
        body: JSON.stringify({ externalArtistId }),
        headers: { 'content-type': 'application/json' },
      }),
      transformResponse: (response: RawServiceConnectionResponse): ServiceConnectionResponse => ({
        connection: withArtistPortalDefaults(response.connection),
      }),
      invalidatesTags: ['ServiceConnection', 'ArtistPortalConnection', 'ArtistPortalOverview', 'ArtistPortalTracks'],
    }),
    syncArtistPortal: builder.mutation<ServiceConnectionResponse, ArtistPortalServiceOptions>({
      query: ({ accountId, integrationType }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}/sync`,
        method: 'POST',
      }),
      transformResponse: (response: RawServiceConnectionResponse): ServiceConnectionResponse => ({
        connection: withArtistPortalDefaults(response.connection),
      }),
      invalidatesTags: ['ServiceConnection', 'ArtistPortalConnection'],
    }),
    disconnectArtistPortal: builder.mutation<ServiceConnectionResponse, ArtistPortalServiceOptions>({
      query: ({ accountId, integrationType }) => ({
        url: `accounts/${accountId}/serviceConnections/${encodeURIComponent(integrationType)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: RawServiceConnectionResponse): ServiceConnectionResponse => ({
        connection: withArtistPortalDefaults(response.connection),
      }),
      invalidatesTags: [
        'ServiceConnection',
        'ConnectionInsight',
        'ArtistPortalConnection',
        'ArtistPortalOverview',
        'ArtistPortalTracks',
      ],
    }),
    duplicateSmartLink: builder.mutation<SmartLinkResponse, DuplicateSmartLinkOptions>({
      query: ({ accountId, smartLinkId, name, slug, theme, backgroundFileId }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/duplicate`,
        method: 'POST',
        body: JSON.stringify({ name, slug, theme, backgroundFileId }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['SmartLinkForList'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData(
              'getSmartLink',
              { smartLinkId: data.smartLink.id, accountId: data.smartLink.accountId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {}
      },
    }),
    removeAccountData: builder.mutation<RemoveAccountDataResponse, RemoveAccountDataOptions>({
      query: (data) => ({
        url: `licenses/removePersonalData`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: () => tagTypes,
    }),
    fetchSmartLinkStats: builder.query<FetchSmartLinkStatsResponse, FetchSmartLinkStatsOptions>({
      query: ({ accountId, smartLinkId, days }) => ({
        url: `accounts/${accountId}/smartLinks/${smartLinkId}/stats${qs.stringify({ days }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
    }),
    fetchSmartLinkStatsSummaries: builder.query<
      FetchSmartLinkStatsSummariesResponse,
      FetchSmartLinkStatsSummariesOptions
    >({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/smartLinkStats${qs.stringify({ days }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
    }),
    fetchSmartLinkThemes: builder.query<FetchSmartLinkThemesResponse, FetchSmartLinkThemesOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/smartLinkThemes`,
        method: 'GET',
      }),
      providesTags: ['SmartLink'],
    }),
    fetchAccountStats: builder.query<FetchAccountStatsResponse, FetchAccountStatsOptions>({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/stats${qs.stringify({ days }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
    }),
    getTrackers: builder.query<GetTrackersResponse, number | string>({
      query: (accountId) => ({
        url: `accounts/${accountId}/trackers`,
        method: 'GET',
      }),
    }),
    createTracker: builder.mutation<TrackerResponse, CreateTrackerOptions>({
      query: ({ accountId, snippet }) => ({
        url: `accounts/${accountId}/trackers`,
        method: 'POST',
        body: JSON.stringify({ snippet }),
        headers: { 'content-type': 'application/json' },
      }),
      async onQueryStarted({ accountId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getTrackers', accountId, (draft) => {
              draft.trackers.push(data.tracker);
              draft.trackers.sort((a, b) => a.id - b.id);
            })
          );
        } catch {}
      },
    }),
    updateTracker: builder.mutation<TrackerResponse, UpdateTrackerOptions>({
      query: ({ accountId, trackerId, snippet }) => ({
        url: `accounts/${accountId}/trackers/${trackerId}`,
        method: 'PATCH',
        body: JSON.stringify({ snippet }),
        headers: { 'content-type': 'application/json' },
      }),
      async onQueryStarted({ accountId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getTrackers', accountId, (draft) => {
              const updatedTrackerIndex = draft.trackers.findIndex((tracker) => tracker.id === data.tracker.id);
              if (updatedTrackerIndex !== -1) {
                draft.trackers[updatedTrackerIndex] = data.tracker;
              }
            })
          );
        } catch {}
      },
    }),
    deleteTracker: builder.mutation<void, DeleteTrackerOptions>({
      query: ({ accountId, trackerId }) => ({
        url: `accounts/${accountId}/trackers/${trackerId}`,
        method: 'DELETE',
      }),
      onQueryStarted({ accountId, trackerId }, { dispatch }) {
        try {
          dispatch(
            fymAPI.util.updateQueryData('getTrackers', accountId, (draft) => {
              const deletedTrackerIndex = draft.trackers.findIndex((tracker) => tracker.id === trackerId);
              if (deletedTrackerIndex !== -1) {
                draft.trackers.splice(deletedTrackerIndex, 1);
              }
            })
          );
        } catch {}
      },
    }),
    // ---------------------------- Promote ----------------------------
    getPromoChannels: builder.query<GetPromoChannelsResponse, GetPromoChannelsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/promo/channels`,
        method: 'GET',
      }),
      providesTags: ['PromoChannels'],
    }),
    patchPromoChannel: builder.mutation<PromoChannelResponse, PatchPromoChannelOptions>({
      query: ({ accountId, platform, handle, status, postingDefaults }) => ({
        url: `accounts/${accountId}/promo/channels/${platform}`,
        method: 'PATCH',
        body: JSON.stringify({ handle, status, postingDefaults }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: CHANNEL_TAGS,
    }),
    // The browser leaves the app for the platform, so this only asks for the
    // address. Nothing is written until the platform sends the browser back.
    startPromoChannelConnect: builder.mutation<StartPromoChannelConnectResponse, StartPromoChannelConnectOptions>({
      query: ({ accountId, platform, returnTo, loginVariant }) => ({
        url: `accounts/${accountId}/promo/channels/${platform}/connect`,
        method: 'POST',
        // `JSON.stringify` leaves out `loginVariant` when it is undefined: the default login.
        body: JSON.stringify({ returnTo, loginVariant }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    // The parked targets expire after 15 minutes, so a cached list would offer
    // pages the artist can no longer pick.
    getPromoConnectTargets: builder.query<GetPromoConnectTargetsResponse, GetPromoConnectTargetsOptions>({
      query: ({ accountId, platform }) => ({
        url: `accounts/${accountId}/promo/channels/${platform}/connect-targets`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    selectPromoConnectTarget: builder.mutation<PromoChannelResponse, SelectPromoConnectTargetOptions>({
      query: ({ accountId, platform, externalId }) => ({
        url: `accounts/${accountId}/promo/channels/${platform}/connect-targets`,
        method: 'POST',
        body: JSON.stringify({ externalId }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: CHANNEL_TAGS,
    }),
    // Drops the tokens. The channel falls back to the manual path, and keeps
    // its handle and its connection identity so it can be reconnected.
    deletePromoChannelConnection: builder.mutation<PromoChannelResponse, DeletePromoChannelConnectionOptions>({
      query: ({ accountId, platform }) => ({
        url: `accounts/${accountId}/promo/channels/${platform}/connection`,
        method: 'DELETE',
      }),
      invalidatesTags: CHANNEL_TAGS,
    }),
    getPromoCampaigns: builder.query<GetPromoCampaignsResponse, GetPromoCampaignsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/promo/campaigns`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [...response.campaigns.map(({ uuid }) => ({ type: 'PromoCampaigns' as const, id: uuid })), 'PromoCampaigns']
          : ['PromoCampaigns'],
    }),
    createPromoCampaign: builder.mutation<PromoCampaignResponse, CreatePromoCampaignOptions>({
      query: ({ accountId, name, kind, catalogItemUuid, smartLinkId, startAt }) => ({
        url: `accounts/${accountId}/promo/campaigns`,
        method: 'POST',
        body: JSON.stringify({ name, kind, catalogItemUuid, smartLinkId, startAt }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['PromoCampaigns'],
    }),
    patchPromoCampaign: builder.mutation<PromoCampaignResponse, PatchPromoCampaignOptions>({
      query: ({ accountId, campaignUuid, name, status }) => ({
        url: `accounts/${accountId}/promo/campaigns/${campaignUuid}`,
        method: 'PATCH',
        body: JSON.stringify({ name, status }),
        headers: { 'content-type': 'application/json' },
      }),
      // Pausing a campaign stops its posts, so the calendar is stale too.
      invalidatesTags: (result, error, { campaignUuid }) => [
        { type: 'PromoCampaigns' as const, id: campaignUuid },
        'PromoCampaigns' as const,
        'PromoPosts' as const,
        'PromoCalendar' as const,
      ],
    }),
    // The Calendar: the posts of a range with their songs, and the release and rollout days.
    getPromoCalendar: builder.query<GetPromoCalendarResponse, GetPromoCalendarOptions>({
      query: ({ accountId, from, to, catalogItemUuid }) => ({
        url: `accounts/${accountId}/promo/calendar?${qs.stringify({ from, to, catalogItemUuid })}`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [
              ...response.posts.map(({ uuid }) => ({ type: 'PromoPosts' as const, id: uuid })),
              'PromoPosts',
              'PromoCalendar',
            ]
          : ['PromoPosts', 'PromoCalendar'],
    }),
    // The scheduled posts the workspace used this month, and its limit (null: no limit).
    getPromoQuota: builder.query<GetPromoQuotaResponse, GetPromoQuotaOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/promo/quota`,
        method: 'GET',
      }),
      providesTags: ['PromoQuota'],
    }),
    // The creator of the connected TikTok channel, read from TikTok now (Direct Post guidelines).
    // Never cached: TikTok wants it read each time the settings open.
    getTikTokCreatorInfo: builder.query<TikTokCreatorInfoAnswer, GetTikTokCreatorInfoOptions>({
      query: ({ accountId, mediaFileId }) => ({
        url: `accounts/${accountId}/promo/channels/tiktok/creator-info${
          mediaFileId === undefined ? '' : `?${qs.stringify({ mediaFileId })}`
        }`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    // The upload rules of the connected YouTube channel (the audit state of the API project).
    getYouTubePostingInfo: builder.query<YouTubePostingInfoAnswer, GetYouTubePostingInfoOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/promo/channels/youtube/posting-info`,
        method: 'GET',
      }),
    }),
    // The AI video flag of the file that the Composer attached (the fixed AI notice). The flag of
    // a file never changes, so the answer needs no tag.
    getPromoMediaInfo: builder.query<PromoMediaInfoAnswer, GetPromoMediaInfoOptions>({
      query: ({ accountId, mediaFileId }) => ({
        url: `accounts/${accountId}/promo/media-info?${qs.stringify({ mediaFileId })}`,
        method: 'GET',
      }),
    }),
    // A move keeps the state: a draft stays a draft and takes no scheduled post.
    movePromoPost: builder.mutation<PromoPostResponse, MovePromoPostOptions>({
      query: ({ accountId, postUuid, scheduledAt }) => ({
        url: `accounts/${accountId}/promo/posts/${postUuid}/move`,
        method: 'POST',
        body: JSON.stringify({ scheduledAt }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { postUuid }) => [
        { type: 'PromoPosts' as const, id: postUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
      ],
    }),
    // Arms a draft at its own time. It takes one scheduled post.
    schedulePromoPost: builder.mutation<PromoPostResponse, SchedulePromoPostOptions>({
      query: ({ accountId, postUuid }) => ({
        url: `accounts/${accountId}/promo/posts/${postUuid}/schedule`,
        method: 'POST',
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { postUuid }) => [
        { type: 'PromoPosts' as const, id: postUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
        'PromoQuota' as const,
      ],
    }),
    // Arms many drafts. Each takes one scheduled post.
    schedulePromoPosts: builder.mutation<SchedulePromoPostsResponse, SchedulePromoPostsOptions>({
      query: ({ accountId, postUuids, expectedCount }) => ({
        url: `accounts/${accountId}/promo/posts/schedule`,
        method: 'POST',
        body: JSON.stringify({ postUuids, expectedCount }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: ['PromoPosts', 'PromoCalendar', 'PromoQuota'],
    }),
    // "Schedule all N posts" of a campaign in one step: one result for each post, never a refusal
    // of the whole request. A dry run changes nothing, so it invalidates nothing.
    launchPromoPosts: builder.mutation<CampaignLaunchResponse, LaunchPromoPostsOptions>({
      query: ({ accountId, postUuids, answers, dryRun }) => ({
        url: `accounts/${accountId}/promo/posts/launch`,
        method: 'POST',
        body: JSON.stringify({ postUuids, answers, dryRun }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { dryRun }) =>
        dryRun === true ? [] : ['PromoPosts', 'PromoCalendar', 'PromoQuota', 'PromoChannels'],
    }),
    // Deletes a draft, a failed or a canceled post.
    deletePromoPost: builder.mutation<void, DeletePromoPostOptions>({
      query: ({ accountId, postUuid }) => ({
        url: `accounts/${accountId}/promo/posts/${postUuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PromoPosts', 'PromoCalendar', 'PromoPerformance'],
    }),
    getPromoPosts: builder.query<GetPromoPostsResponse, GetPromoPostsOptions>({
      query: ({ accountId, from, to }) => ({
        url: `accounts/${accountId}/promo/posts?${qs.stringify({ from, to })}`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [...response.posts.map(({ uuid }) => ({ type: 'PromoPosts' as const, id: uuid })), 'PromoPosts']
          : ['PromoPosts'],
    }),
    createPromoPosts: builder.mutation<CreatePromoPostsResponse, CreatePromoPostsOptions>({
      query: ({
        accountId,
        channelPlatforms,
        caption,
        perChannelCaptions,
        mediaFileId,
        smartLinkId,
        campaignUuid,
        format,
        scheduledAt,
        perChannelScheduledAt,
        asDraft,
        platformSettings,
      }) => ({
        url: `accounts/${accountId}/promo/posts`,
        method: 'POST',
        body: JSON.stringify({
          channelPlatforms,
          caption,
          perChannelCaptions,
          mediaFileId,
          smartLinkId,
          campaignUuid,
          format,
          scheduledAt,
          perChannelScheduledAt,
          asDraft,
          platformSettings,
        }),
        headers: { 'content-type': 'application/json' },
      }),
      // A campaign's progress is counted from its posts, so both lists move.
      invalidatesTags: ['PromoPosts', 'PromoCampaigns', 'PromoCalendar', 'PromoQuota'],
    }),
    patchPromoPost: builder.mutation<PromoPostResponse, PatchPromoPostOptions>({
      query: ({ accountId, postUuid, caption, scheduledAt, status, platformSettings }) => ({
        url: `accounts/${accountId}/promo/posts/${postUuid}`,
        method: 'PATCH',
        // The per-platform settings (TikTok, YouTube) the server needs before it arms a post.
        body: JSON.stringify({ caption, scheduledAt, status, platformSettings }),
        headers: { 'content-type': 'application/json' },
      }),
      // A new time can arm a draft, and a cancel gives a scheduled post back.
      invalidatesTags: (result, error, { postUuid }) => [
        { type: 'PromoPosts' as const, id: postUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
        'PromoQuota' as const,
      ],
    }),
    confirmPromoPostPosted: builder.mutation<PromoPostResponse, ConfirmPromoPostPostedOptions>({
      query: ({ accountId, postUuid }) => ({
        url: `accounts/${accountId}/promo/posts/${postUuid}/confirm-posted`,
        method: 'POST',
        headers: { 'content-type': 'application/json' },
      }),
      // "I posted it" is the only event that makes a post count as published.
      invalidatesTags: (result, error, { postUuid }) => [
        { type: 'PromoPosts' as const, id: postUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
        'PromoPerformance' as const,
      ],
    }),
    getPromoPerformance: builder.query<GetPromoPerformanceResponse, GetPromoPerformanceOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/promo/performance`,
        method: 'GET',
      }),
      providesTags: ['PromoPerformance'],
    }),
    // The social numbers of artist Home. A connect or a disconnect of a channel changes the answer.
    getPromoSocialSummary: builder.query<GetPromoSocialSummaryResponse, GetPromoSocialSummaryOptions>({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/promo/social-summary?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['PromoChannels'],
    }),
    // The Fans screen: the subscribers of the artist. `presave.release.updated` makes both stale.
    getFansSummary: builder.query<PresaveFansSummary, GetFansSummaryOptions>({
      query: ({ accountId, days }) => ({
        url: `accounts/${accountId}/fans/summary?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['FansSummary'],
    }),
    // One page of the releases that went to the subscribers, newest first. The first page has no cursor.
    getFansReleases: builder.query<PresaveFansReleasesPage, GetFansReleasesOptions>({
      query: ({ accountId, cursor }) => ({
        url: `accounts/${accountId}/fans/releases?${qs.stringify({ cursor: cursor ?? undefined })}`,
        method: 'GET',
      }),
      providesTags: ['FansReleases'],
    }),
    // The numbers that the daily read stored. A change of a channel can change them.
    getPromoChannelStats: builder.query<GetPromoChannelStatsResponse, GetPromoChannelStatsOptions>({
      query: ({ accountId, platform, days }) => ({
        url: `accounts/${accountId}/promo/channels/${platform}/stats?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['PromoSocialNumbers', 'PromoChannels'],
    }),
    getPromoSocialPosts: builder.query<GetPromoSocialPostsResponse, GetPromoSocialPostsOptions>({
      query: ({ accountId, days, platform, type, ours, cursor }) => ({
        url: `accounts/${accountId}/promo/social-posts?${qs.stringify({ days, platform, type, ours, cursor })}`,
        method: 'GET',
      }),
      providesTags: ['PromoSocialNumbers', 'PromoChannels'],
    }),
    getPromoSocialPostStats: builder.query<GetPromoSocialPostStatsResponse, GetPromoSocialPostStatsOptions>({
      query: ({ accountId, id, days }) => ({
        url: `accounts/${accountId}/promo/social-posts/${id}/stats?${qs.stringify({ days })}`,
        method: 'GET',
      }),
      providesTags: ['PromoSocialNumbers'],
    }),
    createPromoCaptions: builder.mutation<CreatePromoCaptionsResponse, CreatePromoCaptionsOptions>({
      query: ({ accountId, trackTitle, artistName, releaseDate, angle, platforms }) => ({
        url: `accounts/${accountId}/promo/captions`,
        method: 'POST',
        body: JSON.stringify({ trackTitle, artistName, releaseDate, angle, platforms }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    // ---------------------------- AI Reels ----------------------------
    getReels: builder.query<GetReelsResponse, GetReelsOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/reels${qs.stringify({ catalogItemUuid }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response ? [...response.reels.map(({ uuid }) => ({ type: 'Reels' as const, id: uuid })), 'Reels'] : ['Reels'],
    }),
    getReel: builder.query<ReelResponse, GetReelOptions>({
      query: ({ accountId, reelUuid }) => ({
        url: `accounts/${accountId}/reels/${reelUuid}`,
        method: 'GET',
      }),
      providesTags: (response, error, { reelUuid }) => [{ type: 'Reels' as const, id: reelUuid }],
      // A reel that turns failed gives its video credit back: the credit box reads the credits again.
      async onQueryStarted(args, api) {
        const { dispatch, queryFulfilled } = api;
        const previous = api.getCacheEntry().data?.reel;
        try {
          const { data } = await queryFulfilled;
          if (previous !== undefined && hasNewFailure([previous], [data.reel])) {
            dispatch(fymAPI.util.invalidateTags(VIDEO_CREDIT_TAGS));
          }
        } catch {}
      },
    }),
    createReel: builder.mutation<ReelResponse, CreateReelOptions>({
      // The server reads the song from the catalog row (D2): no audioFileId.
      query: ({ accountId, catalogItemUuid, lyrics, waveform, voiceId, durationSec, instructions }) => ({
        url: `accounts/${accountId}/reels`,
        method: 'POST',
        body: JSON.stringify({ catalogItemUuid, lyrics, waveform, voiceId, durationSec, instructions }),
        headers: { 'content-type': 'application/json' },
      }),
      // A reel uses a video credit and shows on the song page of its song. The Assets badge counts it.
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        'Reels' as const,
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
      ],
      // The Studio card of the AI reel says "1 making" at once (owner request 2026-10-06).
      async onQueryStarted({ accountId, catalogItemUuid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getReleaseKit', { accountId, catalogItemUuid }, (draft) =>
              addMadeReel(draft, data.reel)
            )
          );
        } catch {}
      },
    }),
    approveReel: builder.mutation<ApproveReelResponse, ApproveReelOptions>({
      query: ({ accountId, reelUuid, caption, channelPlatforms }) => ({
        url: `accounts/${accountId}/reels/${reelUuid}/approve`,
        method: 'POST',
        body: JSON.stringify({ caption, channelPlatforms }),
        headers: { 'content-type': 'application/json' },
      }),
      // Approve schedules posts, so the calendar and the campaign progress move too.
      invalidatesTags: (result, error, { reelUuid }) => [
        { type: 'Reels' as const, id: reelUuid },
        'Reels' as const,
        'PromoPosts' as const,
        'PromoCampaigns' as const,
        'ReleaseKit' as const,
        'Rollout' as const,
      ],
    }),
    rejectReel: builder.mutation<ReelWithChildResponse, RejectReelOptions>({
      query: ({ accountId, reelUuid, reason }) => ({
        url: `accounts/${accountId}/reels/${reelUuid}/reject`,
        method: 'POST',
        body: JSON.stringify({ reason }),
        headers: { 'content-type': 'application/json' },
      }),
      // A refused new reel (402, 403, 503) still leaves the parent rejected, so refresh on an error too.
      // The child reel uses a video credit. The Assets badge counts it.
      invalidatesTags: (result, error, { reelUuid }) => [
        { type: 'Reels' as const, id: reelUuid },
        'Reels' as const,
        ...VIDEO_CREDIT_TAGS,
        'ReleaseKit' as const,
        'Work' as const,
      ],
    }),
    regenerateReel: builder.mutation<ReelWithChildResponse, RegenerateReelOptions>({
      query: ({ accountId, reelUuid, reason }) => ({
        url: `accounts/${accountId}/reels/${reelUuid}/regenerate`,
        method: 'POST',
        body: JSON.stringify({ reason }),
        headers: { 'content-type': 'application/json' },
      }),
      // A refused new reel can still store a new reason on the parent, so refresh on an error too.
      // The child reel uses a video credit. The Assets badge counts it.
      invalidatesTags: (result, error, { reelUuid }) => [
        { type: 'Reels' as const, id: reelUuid },
        'Reels' as const,
        ...VIDEO_CREDIT_TAGS,
        'ReleaseKit' as const,
        'Work' as const,
      ],
    }),
    // ---------------------------- Release kits ----------------------------
    // The "N working" / "N failed" badge of the Assets menu entry. `scope: 'assets'` counts only the
    // kit assets and the AI reels, the work that the Assets page shows (no lyric sync). The realtime
    // events of the kit assets and the reels invalidate the `Work` tag.
    getWorkSummary: builder.query<WorkSummary, { accountId: number }>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/work/summary?scope=assets`, method: 'GET' }),
      providesTags: ['Work'],
    }),
    getReleaseKits: builder.query<GetReleaseKitsResponse, GetReleaseKitsOptions>({
      query: ({ accountId, include }) => ({
        url: `accounts/${accountId}/release-kits${qs.stringify({ include }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID }],
    }),
    getReleaseKit: builder.query<GetReleaseKitResponse, GetReleaseKitOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [{ type: 'ReleaseKit' as const, id: catalogItemUuid }],
      // A kit asset or a reel that turns failed gives its credits back (review M17): the credit box
      // reads the credits again.
      async onQueryStarted(args, api) {
        const { dispatch, queryFulfilled } = api;
        const previous = api.getCacheEntry().data;
        try {
          const { data } = await queryFulfilled;
          if (
            previous !== undefined &&
            (hasNewFailure(previous.assets, data.assets) || hasNewFailure(previous.reels, data.reels))
          ) {
            dispatch(fymAPI.util.invalidateTags(VIDEO_CREDIT_TAGS));
          }
        } catch {}
      },
    }),
    createKitAssets: builder.mutation<CreateKitAssetsResult, CreateKitAssetsOptions>({
      query: ({ accountId, catalogItemUuid, items, batch: isBatch }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/assets`,
        method: 'POST',
        body: JSON.stringify({ items, batch: isBatch }),
        headers: { 'content-type': 'application/json' },
      }),
      // New assets change the song page, the list counts, the rollout days, the credits and the
      // Assets badge.
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        { type: 'Rollout' as const, id: catalogItemUuid },
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
      ],
      // The Studio card and the song page say "1 making" at once (owner request 2026-10-06).
      async onQueryStarted({ accountId, catalogItemUuid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getReleaseKit', { accountId, catalogItemUuid }, (draft) =>
              addMadeKitAssets(draft, data.created)
            )
          );
        } catch {}
      },
    }),
    // "Make them again", step 1: the dry run. It charges nothing and makes nothing.
    previewRemakeKitAssets: builder.mutation<RemakeKitAssetsPreview, RemakeKitAssetsOptions>({
      query: ({ accountId, catalogItemUuid, assetUuids }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/assets/remake`,
        method: 'POST',
        body: JSON.stringify({ assetUuids, dryRun: true }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    // "Make them again", step 2: new versions in the theme of the kit, one charge like a batch.
    remakeKitAssets: builder.mutation<CreateKitAssetsResult, RemakeKitAssetsOptions>({
      query: ({ accountId, catalogItemUuid, assetUuids }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/assets/remake`,
        method: 'POST',
        body: JSON.stringify({ assetUuids }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        { type: 'Rollout' as const, id: catalogItemUuid },
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
      ],
      async onQueryStarted({ accountId, catalogItemUuid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getReleaseKit', { accountId, catalogItemUuid }, (draft) =>
              addMadeKitAssets(draft, data.created)
            )
          );
        } catch {}
      },
    }),
    // "Redo the kit with the new cover" (design kit-redo-cover): the state of the kit page. Every
    // kit, reel and cover event invalidates the song's `ReleaseKit` tag, so it follows the renders.
    getCoverRedo: builder.query<CoverRedoState, CoverRedoOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/cover-redo`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [{ type: 'ReleaseKit' as const, id: catalogItemUuid }],
    }),
    // The dry run of the confirm sheet: the state of the chosen groups. It charges nothing.
    previewCoverRedo: builder.mutation<CoverRedoState, StartCoverRedoOptions>({
      query: ({ accountId, catalogItemUuid, kinds, swapPosts }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/cover-redo`,
        method: 'POST',
        body: JSON.stringify({ kinds, swapPosts, dryRun: true }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    // The redo: the confirmed cost goes as `expectedCredits` (409 M24_COVER_REDO_CONFIRM otherwise).
    startCoverRedo: builder.mutation<CoverRedoResult, StartCoverRedoOptions>({
      query: ({ accountId, catalogItemUuid, kinds, swapPosts, expectedCredits }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/cover-redo`,
        method: 'POST',
        body: JSON.stringify({ kinds, swapPosts, expectedCredits }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        { type: 'Rollout' as const, id: catalogItemUuid },
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
        'Releases' as const,
      ],
    }),
    // "Redo the kit with the new release date" (fym-shared `music24/dateRedo.ts`): the state of the
    // Kit and Posts tab. A date change sends `catalog.song.updated`, which invalidates `ReleaseKit`.
    getDateRedo: builder.query<DateRedoState, DateRedoOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/date-redo`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [{ type: 'ReleaseKit' as const, id: catalogItemUuid }],
    }),
    // The dry run of the confirm dialog. It makes nothing.
    previewDateRedo: builder.mutation<DateRedoState, StartDateRedoOptions>({
      query: ({ accountId, catalogItemUuid, swapPosts }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/date-redo`,
        method: 'POST',
        body: JSON.stringify({ swapPosts, dryRun: true }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    // The redo: free, so it needs no confirmed cost.
    startDateRedo: builder.mutation<DateRedoResult, StartDateRedoOptions>({
      query: ({ accountId, catalogItemUuid, swapPosts }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/date-redo`,
        method: 'POST',
        body: JSON.stringify({ swapPosts }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        { type: 'Rollout' as const, id: catalogItemUuid },
        'Work' as const,
        'Releases' as const,
      ],
    }),
    // ---------------------------- Cover editor (spec 2026-10-08 cover editor §5) ----------------------------
    getCoverEditor: builder.query<GetCoverEditorResponse, GetCoverEditorOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/cover-editor/${catalogItemUuid}`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [{ type: 'CoverEditor' as const, id: catalogItemUuid }],
    }),
    createCoverDesign: builder.mutation<CoverDesignResponse, CreateCoverDesignOptions>({
      query: ({ accountId, catalogItemUuid, doc, name }) => ({
        url: `accounts/${accountId}/cover-editor/${catalogItemUuid}/designs`,
        method: 'POST',
        body: JSON.stringify({ doc, name }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (response, error, { catalogItemUuid }) => [
        { type: 'CoverEditor' as const, id: catalogItemUuid },
      ],
    }),
    // The autosave. It invalidates nothing: the editor holds the document, and a read after each
    // save would replace what the user types.
    updateCoverDesign: builder.mutation<CoverDesignResponse, UpdateCoverDesignOptions>({
      query: ({ accountId, designUuid, doc, name }) => ({
        url: `accounts/${accountId}/cover-editor/designs/${designUuid}`,
        method: 'PATCH',
        body: JSON.stringify({ doc, name }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    deleteCoverDesign: builder.mutation<void, DeleteCoverDesignOptions>({
      query: ({ accountId, designUuid }) => ({
        url: `accounts/${accountId}/cover-editor/designs/${designUuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (response, error, { catalogItemUuid }) => [
        { type: 'CoverEditor' as const, id: catalogItemUuid },
      ],
    }),
    createCoverAiRun: builder.mutation<CreateCoverAiRunResponse, CreateCoverAiRunOptions>({
      query: ({ accountId, catalogItemUuid, instructions, referenceFileIds, referenceRightsConfirmed }) => ({
        url: `accounts/${accountId}/cover-editor/${catalogItemUuid}/ai-runs`,
        method: 'POST',
        body: JSON.stringify({ instructions, referenceFileIds, referenceRightsConfirmed }),
        headers: { 'content-type': 'application/json' },
      }),
      // The new pictures, the month count and the rights flag. A refusal (402, 403) reads the count too.
      invalidatesTags: (response, error, { catalogItemUuid }) => [
        { type: 'CoverEditor' as const, id: catalogItemUuid },
      ],
    }),
    applyCoverDesign: builder.mutation<ApplyCoverDesignResponse, ApplyCoverDesignOptions>({
      query: ({ accountId, designUuid, fileId }) => ({
        url: `accounts/${accountId}/cover-editor/designs/${designUuid}/apply`,
        method: 'POST',
        body: JSON.stringify({ fileId }),
        headers: { 'content-type': 'application/json' },
      }),
      // The song has a new cover: the catalog, the song page, the kit (palette, stale assets) and the releases.
      invalidatesTags: (response, error, { catalogItemUuid }) => [
        { type: 'CoverEditor' as const, id: catalogItemUuid },
        'CatalogItem' as const,
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        'Releases' as const,
      ],
    }),
    // The bank is public and the same for everybody.
    getCoverBackgrounds: builder.query<GetCoverBackgroundsResponse, void>({
      query: () => ({ url: 'public/cover-backgrounds', method: 'GET' }),
      providesTags: ['CoverBackgrounds'],
      keepUnusedDataFor: 60 * 60,
    }),
    // The public cover maker (no session): the email and the design of a visitor who downloads.
    createCoverMakerLead: builder.mutation<CreateCoverMakerLeadResponse, CreateCoverMakerLeadOptions>({
      query: (body) => ({
        url: 'public/cover-maker/leads',
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    getCoverMakerLead: builder.query<GetCoverMakerLeadResponse, { token: string }>({
      query: ({ token }) => ({ url: `public/cover-maker/leads/${encodeURIComponent(token)}`, method: 'GET' }),
      keepUnusedDataFor: 0,
    }),
    // ---------------------------- Asset share links (design music24-asset-viewer) ----------------------------
    getAssetShare: builder.query<AssetShareResponse, AssetShareOptions>({
      query: ({ accountId, kind, assetUuid }) => ({
        url: `accounts/${accountId}/asset-shares?${qs.stringify({ kind, assetUuid })}`,
        method: 'GET',
      }),
      providesTags: (response, error, { kind, assetUuid }) => [
        { type: 'AssetShare' as const, id: `${kind}:${assetUuid}` },
      ],
    }),
    createAssetShare: builder.mutation<AssetShareResponse, AssetShareOptions>({
      query: ({ accountId, kind, assetUuid }) => ({
        url: `accounts/${accountId}/asset-shares`,
        method: 'POST',
        body: JSON.stringify({ kind, assetUuid }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (response, error, { kind, assetUuid }) => [
        { type: 'AssetShare' as const, id: `${kind}:${assetUuid}` },
      ],
    }),
    deleteAssetShare: builder.mutation<void, AssetShareOptions>({
      query: ({ accountId, kind, assetUuid }) => ({
        url: `accounts/${accountId}/asset-shares?${qs.stringify({ kind, assetUuid })}`,
        method: 'DELETE',
      }),
      invalidatesTags: (response, error, { kind, assetUuid }) => [
        { type: 'AssetShare' as const, id: `${kind}:${assetUuid}` },
      ],
    }),
    // ---------------------------- One-off publish channels (YouTube, Spotify Canvas) ----------------------------
    // One list per account (and per song): the badges of every asset card read it. The socket
    // event `asset.publish.updated` makes it stale.
    getAssetPublishes: builder.query<AssetPublishListResponse, { accountId: number; catalogItemUuid?: string }>({
      // `channels=all` adds the manual marks of the Kit tab (Apple Music, the banners, the
      // thumbnail); the API keeps the four old channels by default for the native apps.
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/asset-publishes?${qs.stringify({ channels: 'all', catalogItemUuid })}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'AssetPublish' as const, id: 'LIST' }],
    }),
    getAssetPublishDefaults: builder.query<
      AssetPublishDefaultsResponse,
      { accountId: number; kitAssetUuid: string; channel: OneOffChannel }
    >({
      query: ({ accountId, kitAssetUuid, channel }) => ({
        url: `accounts/${accountId}/asset-publishes/defaults?${qs.stringify({ kitAssetUuid, channel })}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    createAssetPublish: builder.mutation<AssetPublishResponse, { accountId: number; body: CreateAssetPublishBody }>({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/asset-publishes`,
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: [{ type: 'AssetPublish' as const, id: 'LIST' }],
    }),
    updateAssetPublish: builder.mutation<
      AssetPublishResponse,
      { accountId: number; publishUuid: string; body: UpdateAssetPublishBody }
    >({
      query: ({ accountId, publishUuid, body }) => ({
        url: `accounts/${accountId}/asset-publishes/${encodeURIComponent(publishUuid)}`,
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: [{ type: 'AssetPublish' as const, id: 'LIST' }],
    }),
    deleteAssetPublish: builder.mutation<AssetPublishResponse, { accountId: number; publishUuid: string }>({
      query: ({ accountId, publishUuid }) => ({
        url: `accounts/${accountId}/asset-publishes/${encodeURIComponent(publishUuid)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'AssetPublish' as const, id: 'LIST' }],
    }),
    // ---------------------------- Song publishes (SoundCloud) ----------------------------
    // The uploads of a song to an audio store (spec 2026-10-15 D14). The socket event
    // `song.publish.updated` makes the list of the song and the account list stale.
    getSongPublishes: builder.query<SongPublishListResponse, { accountId: number; catalogItemUuid?: string }>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/song-publishes${qs.stringify({ catalogItemUuid }, { addQueryPrefix: true })}`,
        method: 'GET',
      }),
      providesTags: (result, error, { catalogItemUuid }) => [
        { type: 'SongPublish' as const, id: catalogItemUuid ?? 'LIST' },
      ],
    }),
    getSongPublishDefaults: builder.query<
      SongPublishDefaultsResponse,
      { accountId: number; catalogItemUuid: string; channel: CreateSongPublishBody['channel'] }
    >({
      query: ({ accountId, catalogItemUuid, channel }) => ({
        url: `accounts/${accountId}/song-publishes/defaults?${qs.stringify({ catalogItemUuid, channel })}`,
        method: 'GET',
      }),
      // The master can finish while the dialog is open (`catalog.song.updated`).
      providesTags: (result, error, { catalogItemUuid }) => [
        { type: 'SongPublish' as const, id: catalogItemUuid },
        { type: 'CatalogItem' as const, id: catalogItemUuid },
      ],
      keepUnusedDataFor: 0,
    }),
    createSongPublish: builder.mutation<SongPublishResponse, { accountId: number; body: CreateSongPublishBody }>({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/song-publishes`,
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { body }) => [
        { type: 'SongPublish' as const, id: body.catalogItemUuid },
        { type: 'SongPublish' as const, id: 'LIST' },
        'ServiceConnection',
      ],
    }),
    updateSongPublish: builder.mutation<
      SongPublishResponse,
      { accountId: number; catalogItemUuid: string; publishUuid: string; body: UpdateSongPublishBody }
    >({
      query: ({ accountId, publishUuid, body }) => ({
        url: `accounts/${accountId}/song-publishes/${encodeURIComponent(publishUuid)}`,
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'SongPublish' as const, id: catalogItemUuid },
        { type: 'SongPublish' as const, id: 'LIST' },
      ],
    }),
    deleteSongPublish: builder.mutation<
      SongPublishResponse,
      { accountId: number; catalogItemUuid: string; publishUuid: string }
    >({
      query: ({ accountId, publishUuid }) => ({
        url: `accounts/${accountId}/song-publishes/${encodeURIComponent(publishUuid)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'SongPublish' as const, id: catalogItemUuid },
        { type: 'SongPublish' as const, id: 'LIST' },
        'ServiceConnection',
      ],
    }),
    // ---------------------------- Spotify Canvas upload ----------------------------
    // The row "Spotify Canvas" of "Where it plays" (design spotify-canvas a, b). The socket events
    // `asset.publish.updated` (a Canvas record) and `artistPortal.synced` (the Spotify for Artists
    // sign-in) make the tag stale. The settings (the auto upload switch) are one per artist.
    getSpotifyCanvas: builder.query<SpotifyCanvasSongResponse, { accountId: number; catalogItemUuid: string }>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/spotify-canvas?${qs.stringify({ catalogItemUuid })}`,
        method: 'GET',
      }),
      providesTags: (result, error, { catalogItemUuid }) => [
        { type: 'SpotifyCanvas' as const, id: catalogItemUuid },
        { type: 'SpotifyCanvas' as const, id: 'SETTINGS' },
        // A new Canvas loop of the kit, or a new Spotify link of the song, changes the state.
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'CatalogItem' as const, id: catalogItemUuid },
      ],
    }),
    startSpotifyCanvas: builder.mutation<
      SpotifyCanvasSongResponse,
      { accountId: number; body: StartSpotifyCanvasBody }
    >({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/spotify-canvas`,
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { body }) => [
        { type: 'SpotifyCanvas' as const, id: body.catalogItemUuid },
        { type: 'SpotifyCanvas' as const, id: 'SETTINGS' },
        { type: 'AssetPublish' as const, id: 'LIST' },
      ],
    }),
    getSpotifyCanvasSettings: builder.query<SpotifyCanvasSettingsResponse, { accountId: number }>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/spotify-canvas/settings`,
        method: 'GET',
      }),
      providesTags: [{ type: 'SpotifyCanvas' as const, id: 'SETTINGS' }],
    }),
    updateSpotifyCanvasSettings: builder.mutation<
      SpotifyCanvasSettingsResponse,
      { accountId: number; body: UpdateSpotifyCanvasSettingsBody }
    >({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/spotify-canvas/settings`,
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      }),
      // Every Canvas row reads the settings (the Auto chip), so the whole type is stale.
      invalidatesTags: ['SpotifyCanvas'],
    }),
    // The public page of a share link (`/app/a/<token>`). Anonymous: the token is the key.
    getPublicAssetShare: builder.query<PublicAssetShare, { token: string }>({
      query: ({ token }) => ({
        url: `public/asset-shares/${encodeURIComponent(token)}`,
        method: 'GET',
      }),
    }),
    // Where a signed-in member opens the shared asset. `access` is null for every other caller.
    getAssetShareAccess: builder.query<AssetShareAccessResponse, { token: string }>({
      query: ({ token }) => ({
        url: `public/asset-shares/${encodeURIComponent(token)}/access`,
        method: 'GET',
      }),
      // The answer depends on the session: never reuse it for another user.
      keepUnusedDataFor: 0,
    }),
    getKitAssetVersions: builder.query<GetKitAssetVersionsResponse, GetKitAssetVersionsOptions>({
      query: ({ accountId, catalogItemUuid, assetUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/assets/${assetUuid}/versions`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [{ type: 'ReleaseKit' as const, id: catalogItemUuid }],
    }),
    updateKitLook: builder.mutation<UpdateKitLookResponse, UpdateKitLookOptions>({
      query: ({ accountId, catalogItemUuid, themeId, fontOverride, logoFileId, paletteOverride, saveAsDefault }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/look`,
        method: 'PATCH',
        body: JSON.stringify({ themeId, fontOverride, logoFileId, paletteOverride, saveAsDefault }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid, saveAsDefault }) => [
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        ...(saveAsDefault === true ? ['ReleaseKitLook' as const] : []),
      ],
    }),
    getReleaseKitLook: builder.query<GetReleaseKitLookResponse, ReleaseKitLookOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/release-kit-look`,
        method: 'GET',
      }),
      providesTags: ['ReleaseKitLook'],
    }),
    getAccountTimeZone: builder.query<AccountTimeZoneResponse, AccountTimeZoneOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/time-zone`,
        method: 'GET',
      }),
      providesTags: (response, error, { accountId }) => [{ type: 'AccountTimeZone' as const, id: accountId }],
    }),
    // A new zone changes the default times of the plan steps that have no post yet.
    updateAccountTimeZone: builder.mutation<AccountTimeZoneResponse, UpdateAccountTimeZoneOptions>({
      query: ({ accountId, timeZone }) => ({
        url: `accounts/${accountId}/time-zone`,
        method: 'PUT',
        // The base query sends the body as is: a plain object went out as "[object Object]" (QA brk-r2-2).
        body: JSON.stringify({ timeZone }),
      }),
      invalidatesTags: (response, error, { accountId }) => [
        { type: 'AccountTimeZone' as const, id: accountId },
        'Rollout',
      ],
    }),
    createKitZip: builder.mutation<CreateKitZipResponse, CreateKitZipOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/zip`,
        method: 'POST',
      }),
    }),
    getKitZip: builder.query<GetKitZipResponse, GetKitZipOptions>({
      query: ({ accountId, catalogItemUuid, requestId }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/zip/${encodeURIComponent(requestId)}`,
        method: 'GET',
      }),
      providesTags: (response, error, { requestId }) => [{ type: 'KitZip' as const, id: requestId }],
    }),
    // The clean-copies run after the first paid plan (trial upsell, design b5). The kit asset and
    // reel events make it stale (`realtimeEventTags`).
    getCleanCopies: builder.query<GetCleanCopiesResponse, GetCleanCopiesOptions>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/clean-copies`, method: 'GET' }),
      providesTags: ['CleanCopies'],
    }),
    getKitProRes: builder.query<KitProResResponse, KitProResOptions>({
      query: ({ accountId, catalogItemUuid, assetUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/assets/${assetUuid}/prores`,
        method: 'GET',
      }),
      providesTags: (response, error, { assetUuid }) => [{ type: 'KitProRes' as const, id: assetUuid }],
    }),
    requestKitProRes: builder.mutation<KitProResResponse, KitProResOptions>({
      query: ({ accountId, catalogItemUuid, assetUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/assets/${assetUuid}/prores`,
        method: 'POST',
      }),
      invalidatesTags: (response, error, { assetUuid }) => [{ type: 'KitProRes' as const, id: assetUuid }],
    }),
    getRollout: builder.query<GetRolloutResponse, GetRolloutOptions>({
      query: ({ accountId, catalogItemUuid, kind, platforms }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/rollout${qs.stringify(
          { kind, platforms: platforms.join(',') },
          { addQueryPrefix: true }
        )}`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [{ type: 'Rollout' as const, id: catalogItemUuid }],
    }),
    addRolloutDrafts: builder.mutation<AddRolloutDraftsResponse, AddRolloutDraftsOptions>({
      query: ({ accountId, catalogItemUuid, kind, platforms, dayKeys, makeMissing, autoDraft }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/rollout`,
        method: 'POST',
        body: JSON.stringify({ kind, platforms, dayKeys, makeMissing, autoDraft }),
        headers: { 'content-type': 'application/json' },
      }),
      // The drafts land on the calendar, in the song's campaign, and the rollout marks the days added.
      // A queued asset changes the song page, the credits and the Assets badge too.
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        'PromoPosts' as const,
        'PromoCampaigns' as const,
        'PromoCalendar' as const,
        { type: 'Rollout' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        'Releases' as const,
        ...((result?.queued?.length ?? 0) > 0 ? [...VIDEO_CREDIT_TAGS, 'Work' as const] : []),
      ],
    }),
    // ---------------------------- Releases v2 (design music24-releases-v2) ----------------------------
    // Every catalog song with its group, setup, counts and next step (design a1).
    getReleases: builder.query<GetReleasesResponse, GetReleasesOptions>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/releases`, method: 'GET' }),
      providesTags: ['Releases'],
    }),
    // The Overview of one release (design b1-b3).
    getReleaseOverview: builder.query<ReleaseOverview, ReleaseOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [
        'Releases',
        { type: 'Releases' as const, id: catalogItemUuid },
        'PromoPosts',
      ],
    }),
    // "Send to N subscribers" (design presave-subscriptions d2): a song released with no pre-save goes
    // to the artist subscribers. 400 not eligible, 409 already sent; a 402 opens the global prompt.
    sendFanRelease: builder.mutation<{ releaseUuid: string }, ReleaseOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/catalog/${catalogItemUuid}/presave-release`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Releases' as const, id: catalogItemUuid },
        'FansReleases' as const,
        'FansSummary' as const,
      ],
    }),
    // The view mark of this user (the Assets tab opened). The list counts ("N new") and the
    // Overview "new" items change.
    markReleaseViewed: builder.mutation<MarkReleaseViewedResponse, ReleaseOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/viewed`,
        method: 'POST',
      }),
      async onQueryStarted({ accountId, catalogItemUuid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            fymAPI.util.updateQueryData('getReleases', { accountId }, (draft) => {
              const row = draft.releases.find((one) => one.catalogItemUuid === catalogItemUuid);
              if (row !== undefined) {
                row.lastViewedAt = data.viewedAt;
                row.assets.newCount = 0;
              }
            })
          );
          // The Overview counts its "new" items from `lastViewedAt` too.
          dispatch(
            fymAPI.util.updateQueryData('getReleaseOverview', { accountId, catalogItemUuid }, (draft) => {
              draft.lastViewedAt = data.viewedAt;
            })
          );
        } catch {}
      },
    }),
    // The Plan tab: the rollout days with their posts and gaps (design f1-f3).
    getReleasePlan: builder.query<ReleasePlan, GetReleasePlanOptions>({
      query: ({ accountId, catalogItemUuid, kind, platforms, timeZone }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan${qs.stringify(
          { kind, platforms: platforms.join(','), timeZone },
          { addQueryPrefix: true }
        )}`,
        method: 'GET',
      }),
      providesTags: (response, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        'PromoPosts',
        'PromoCalendar',
      ],
    }),
    // ---- The steps of the rollout plan (design rollout-plan c1, c2, c3; owner decisions 2026-10-09) ----
    // "Change channels": the default channels of a step with no list of its own.
    setPlanChannels: builder.mutation<{ campaignUuid: string; defaultPlatforms: string[] }, SetPlanChannelsOptions>({
      query: ({ accountId, catalogItemUuid, kind, platforms, channels }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan/channels`,
        method: 'PUT',
        body: JSON.stringify({ kind, platforms, channels }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [{ type: 'Rollout' as const, id: catalogItemUuid }],
    }),
    // The channels of one step: a new channel gets a draft, a channel taken off loses its open post.
    setPlanStepChannels: builder.mutation<SetPlanStepChannelsResponse, SetPlanStepChannelsOptions>({
      query: ({ accountId, catalogItemUuid, kind, dayKey, platforms, channels }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan/steps/${dayKey}/channels`,
        method: 'PUT',
        body: JSON.stringify({ kind, platforms, channels }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
        'PromoQuota' as const,
        'Releases' as const,
      ],
    }),
    // "Skip this step": its open posts leave the plan (a canceled post gives its unit back this month).
    skipPlanStep: builder.mutation<SkipPlanStepResponse, PlanStepOptions>({
      query: ({ accountId, catalogItemUuid, kind, dayKey, platforms }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan/steps/${dayKey}/skip`,
        method: 'POST',
        body: JSON.stringify({ kind, platforms }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
        'PromoQuota' as const,
        'Releases' as const,
      ],
    }),
    // "Undo skip": the step comes back with no posts. A step of an AI reels plan (`kind: 'reels'`)
    // gets the drafts of its ready reel again at once: the post lists and the Calendar read them too.
    unskipPlanStep: builder.mutation<{ skippedKeys: string[] }, PlanStepOptions>({
      query: ({ accountId, catalogItemUuid, kind, dayKey }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan/steps/${dayKey}/skip${qs.stringify(
          { kind },
          { addQueryPrefix: true }
        )}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { catalogItemUuid, kind }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        'Releases' as const,
        ...(kind === 'reels' ? ['PromoPosts' as const, 'PromoCalendar' as const] : []),
      ],
    }),
    // The media swap of a step: the file of every open post of the step changes.
    swapPlanStepMedia: builder.mutation<{ changed: number }, SwapPlanStepMediaOptions>({
      query: ({ accountId, catalogItemUuid, kind, dayKey, platforms, fileId }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan/steps/${dayKey}/media`,
        method: 'POST',
        body: JSON.stringify({ kind, platforms, fileId }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        'PromoPosts' as const,
        'PromoCalendar' as const,
      ],
    }),
    // "Make an AI reel" of a step: the reel with the default settings (1 video credit).
    makePlanStepReel: builder.mutation<{ reelUuid: string }, PlanStepOptions>({
      query: ({ accountId, catalogItemUuid, kind, dayKey, platforms }) => ({
        url: `accounts/${accountId}/releases/${catalogItemUuid}/plan/steps/${dayKey}/reel`,
        method: 'POST',
        body: JSON.stringify({ kind, platforms }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        'Reels' as const,
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
        'Releases' as const,
      ],
    }),
    // ---- The AI reels plan (design rollout-plan f1-f6, spec 2026-10-12): the rollout kind `reels` ----
    // The dry run of the setup: the days, the counts, the credits left and the first reason that
    // stops the plan. It changes nothing, so it is a query; the body goes as a POST.
    previewReelsPlan: builder.query<ReelsPlanPreview, ReelsPlanOptions>({
      query: ({ accountId, catalogItemUuid, startDay, days, cadence, postsPerDay, platforms }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/reels-plan`,
        method: 'POST',
        body: JSON.stringify({ startDay, days, cadence, postsPerDay, platforms, dryRun: true }),
        headers: { 'content-type': 'application/json' },
      }),
      // The answer holds the credits left, the channels check and the "plan exists" refusal. It has
      // its own tag: only the two writes of the plan, a change of the credit balance and a change
      // of the channels read it again. The tag `Rollout` would send this POST again on each
      // `reel.updated` event while the reels of a plan render.
      providesTags: ['ReelsPlanPreview'],
    }),
    // "Make the plan": every AI reel of the plan with one charge (all or none), one step for each post.
    makeReelsPlan: builder.mutation<ReelsPlanMade, ReelsPlanOptions>({
      query: ({ accountId, catalogItemUuid, startDay, days, cadence, postsPerDay, platforms }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/reels-plan`,
        method: 'POST',
        body: JSON.stringify({ startDay, days, cadence, postsPerDay, platforms }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
        'Reels' as const,
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
        'PromoCampaigns' as const,
        'PromoCalendar' as const,
        'Releases' as const,
      ],
    }),
    // "End the plan": its drafts and failed posts are deleted, its scheduled posts are canceled.
    endReelsPlan: builder.mutation<ReelsPlanEnded, ReleaseOptions>({
      query: ({ accountId, catalogItemUuid }) => ({
        url: `accounts/${accountId}/release-kits/${catalogItemUuid}/reels-plan`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => [
        { type: 'Rollout' as const, id: catalogItemUuid },
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        'PromoPosts' as const,
        'PromoCampaigns' as const,
        'PromoCalendar' as const,
        'PromoQuota' as const,
        ...VIDEO_CREDIT_TAGS,
        'Work' as const,
        'Releases' as const,
      ],
    }),
    // ---------------------------- Artist Profile (Studio → Profile) ----------------------------
    // Spec 2026-10-17 artist Profile. One tag per artist (`ArtistProfile`, id = account id): every
    // write makes the profile and the version lists stale. The realtime event of a profile image
    // reads it again (`realtimeEventTags.ts`).
    getArtistProfile: builder.query<ArtistProfileResponse, ArtistProfileOptions>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/artist-profile`, method: 'GET' }),
      providesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    makeProfileAssets: builder.mutation<ProfileMakeResponse, MakeProfileAssetsOptions>({
      query: ({ accountId, items }) => ({
        url: `accounts/${accountId}/artist-profile/assets`,
        method: 'POST',
        body: JSON.stringify({ items }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    useReleaseForProfile: builder.mutation<ProfileMakeResponse, UseReleaseForProfileOptions>({
      query: ({ accountId, catalogItemUuid, kitAssetUuid }) => ({
        url: `accounts/${accountId}/artist-profile/from-release`,
        method: 'POST',
        body: JSON.stringify(kitAssetUuid === undefined ? { catalogItemUuid } : { catalogItemUuid, kitAssetUuid }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    getProfileAssetVersions: builder.query<ProfileAssetVersionsResponse, ProfileAssetOptions>({
      query: ({ accountId, assetUuid }) => ({
        url: `accounts/${accountId}/artist-profile/assets/${encodeURIComponent(assetUuid)}/versions`,
        method: 'GET',
      }),
      providesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    setProfileAssetCurrent: builder.mutation<ProfileAssetResponse, ProfileAssetOptions>({
      query: ({ accountId, assetUuid }) => ({
        url: `accounts/${accountId}/artist-profile/assets/${encodeURIComponent(assetUuid)}/current`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    markProfileAsset: builder.mutation<ProfileAssetResponse, MarkProfileAssetOptions>({
      query: ({ accountId, assetUuid, set, service }) => ({
        url: `accounts/${accountId}/artist-profile/assets/${encodeURIComponent(assetUuid)}/mark`,
        method: 'POST',
        body: JSON.stringify(service === undefined ? { set } : { set, service }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    saveArtistBio: builder.mutation<SaveArtistBioResponse, SaveArtistBioOptions>({
      query: ({ accountId, bioShort, bioLong }) => ({
        url: `accounts/${accountId}/artist-profile/bio`,
        method: 'PUT',
        body: JSON.stringify({ bioShort, bioLong }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    // A draft saves nothing, so it makes no tag stale.
    draftArtistBio: builder.mutation<ArtistBioDraftResponse, DraftArtistBioOptions>({
      query: ({ accountId, facts }) => ({
        url: `accounts/${accountId}/artist-profile/bio/draft`,
        method: 'POST',
        body: JSON.stringify(facts === undefined || facts === '' ? {} : { facts }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    createProfileZip: builder.mutation<ProfileZipResponse, ArtistProfileOptions>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/artist-profile/zip`, method: 'POST' }),
    }),
    uploadProfileToSpotify: builder.mutation<SpotifyProfileUploadResponse, UploadProfileToSpotifyOptions>({
      query: ({ accountId, images, acceptRights }) => ({
        url: `accounts/${accountId}/artist-profile/spotify`,
        method: 'POST',
        body: JSON.stringify(acceptRights === true ? { images, acceptRights } : { images }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    uploadBioToSpotify: builder.mutation<SpotifyBioUploadResponse, UploadBioToSpotifyOptions>({
      query: ({ accountId, acceptRights }) => ({
        url: `accounts/${accountId}/artist-profile/spotify/bio`,
        method: 'POST',
        body: JSON.stringify(acceptRights === true ? { acceptRights } : {}),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    // Profile "Send to all" (2026-10-20, fym-shared `music24/profileSend.ts`). The same tag as the
    // Profile: the event `profile.send.updated` reads the sheet and the sends again.
    getProfileSendOverview: builder.query<ProfileSendOverviewResponse, ArtistProfileOptions>({
      query: ({ accountId }) => ({ url: `accounts/${accountId}/artist-profile/send`, method: 'GET' }),
      providesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    getProfileSend: builder.query<ProfileSendResponse, ProfileSendOptions>({
      query: ({ accountId, sendUuid }) => ({
        url: `accounts/${accountId}/artist-profile/send/${encodeURIComponent(sendUuid)}`,
        method: 'GET',
      }),
      providesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    startProfileSend: builder.mutation<ProfileSendResponse, StartProfileSendOptions>({
      query: ({ accountId, targets, rightsConfirmed }) => ({
        url: `accounts/${accountId}/artist-profile/send`,
        method: 'POST',
        body: JSON.stringify(rightsConfirmed === true ? { targets, rightsConfirmed } : { targets }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    saveProfileSendSettings: builder.mutation<ProfileSendSettingsResponse, SaveProfileSendSettingsOptions>({
      query: ({ accountId, keepInSync }) => ({
        url: `accounts/${accountId}/artist-profile/send/settings`,
        method: 'PUT',
        body: JSON.stringify({ keepInSync }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { accountId }) => [{ type: 'ArtistProfile' as const, id: accountId }],
    }),
    // ---------------------------- Video credits ----------------------------
    getVideoCredits: builder.query<VideoCredits, GetVideoCreditsOptions>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/credits`,
        method: 'GET',
      }),
      transformResponse: (response: GetVideoCreditsResponse) => toVideoCredits(response),
      providesTags: ['VideoCredits'],
    }),
  }),
});

export const {
  useGetUserQuery,
  useSendFeedbackMutation,
  useSendWishMutation,
  useGetAccountQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useGetSmartLinksQuery,
  useGetSmartLinkQuery,
  useUpdateSmartLinkMutation,
  usePublishSmartLinkMutation,
  useGetBioLinkSettingsQuery,
  useUpdateBioLinkSettingsMutation,
  useUnpublishSmartLinkMutation,
  useCreateSmartLinkMutation,
  useVerifySmartLinkSlugMutation,
  useVerifyAccountNameSlugMutation,
  useDeleteSmartLinkMutation,
  useUpdateWidgetMutation,
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
  useCreateWizardReleaseLinkMutation,
  useCreateWizardPlaylistLinkMutation,
  useCreateWizardPresaveLinkMutation,
  useGetPresaveCampaignQuery,
  useCreatePresaveCampaignForSmartLinkMutation,
  useUpdatePresaveCampaignMutation,
  useReleasePresaveCampaignMutation,
  useCancelPresaveCampaignMutation,
  useGetCatalogQuery,
  useClaimCatalogItemMutation,
  useAddReleasedCatalogItemMutation,
  useAddUnreleasedCatalogItemMutation,
  usePatchCatalogItemMutation,
  useDeleteCatalogItemMutation,
  useGetCatalogSongQuery,
  usePatchCatalogSongMutation,
  useGetLyricTimingQuery,
  usePatchLyricTimingMutation,
  useResyncLyricTimingMutation,
  useCreateMasterUploadMutation,
  useCreateMasterStreamLinkMutation,
  useCreateMasterDownloadLinkMutation,
  useCompleteMasterUploadMutation,
  useAbortMasterUploadMutation,
  useCreatePostVideoUploadMutation,
  useCompletePostVideoUploadMutation,
  useAbortPostVideoUploadMutation,
  useGetCatalogReleaseEventsQuery,
  useDismissCatalogReleaseEventMutation,
  useLinkCatalogItemMutation,
  useMarkCatalogItemReleasedMutation,
  useGetClaimMatchQuery,
  useGetCatalogMergeCandidatesQuery,
  useMergeCatalogItemsMutation,
  useGetCatalogSuggestionsQuery,
  useDismissCatalogSuggestionMutation,
  useGetServiceConnectionsQuery,
  useGetConnectionInsightsQuery,
  usePatchServiceConnectionMutation,
  useRequestServiceConnectionMutation,
  useRequestEarlyAccessMutation,
  useGetArtistPortalOverviewQuery,
  useGetArtistPortalTracksQuery,
  useGetArtistPortalTrackDailyQuery,
  useGetArtistPortalPlaylistsQuery,
  useGetStreamingOverviewQuery,
  useGetStreamingTracksQuery,
  useGetDittoStreamsByDimensionQuery,
  useGetStreamingDimensionQuery,
  useGetStreamingAudienceQuery,
  useGetStreamingPlaySourcesQuery,
  useGetStreamingShortVideoQuery,
  useGetStreamingAcceleratorQuery,
  useGetStreamingEarningsQuery,
  useBeginArtistPortalAuthMutation,
  useGetArtistPortalArtistsMutation,
  useCompleteArtistPortalAuthMutation,
  useSelectArtistPortalArtistMutation,
  useSyncArtistPortalMutation,
  useDisconnectArtistPortalMutation,
  useRemoveAccountDataMutation,
  useFetchSmartLinkStatsQuery,
  useGetAccountsQuery,
  useFetchAccountStatsQuery,
  useFetchSmartLinkStatsSummariesQuery,
  useFetchSmartLinkThemesQuery,
  useGetTrackersQuery,
  useCreateTrackerMutation,
  useUpdateTrackerMutation,
  useDeleteTrackerMutation,
  useDuplicateSmartLinkMutation,
  usePostFileMutation,
  useGetPromoChannelsQuery,
  usePatchPromoChannelMutation,
  useStartPromoChannelConnectMutation,
  useGetPromoConnectTargetsQuery,
  useSelectPromoConnectTargetMutation,
  useDeletePromoChannelConnectionMutation,
  useGetPromoCampaignsQuery,
  useCreatePromoCampaignMutation,
  usePatchPromoCampaignMutation,
  useGetPromoPostsQuery,
  useGetPromoCalendarQuery,
  useGetPromoQuotaQuery,
  useGetTikTokCreatorInfoQuery,
  useGetYouTubePostingInfoQuery,
  useGetPromoMediaInfoQuery,
  useMovePromoPostMutation,
  useSchedulePromoPostMutation,
  useSchedulePromoPostsMutation,
  useLaunchPromoPostsMutation,
  useDeletePromoPostMutation,
  useCreatePromoPostsMutation,
  usePatchPromoPostMutation,
  useConfirmPromoPostPostedMutation,
  useGetPromoPerformanceQuery,
  useGetPromoSocialSummaryQuery,
  useGetFansSummaryQuery,
  useGetFansReleasesQuery,
  useSendFanReleaseMutation,
  useGetPromoChannelStatsQuery,
  useGetPromoSocialPostsQuery,
  useLazyGetPromoSocialPostsQuery,
  useGetPromoSocialPostStatsQuery,
  useCreatePromoCaptionsMutation,
  useGetReelsQuery,
  useGetReelQuery,
  useCreateReelMutation,
  useApproveReelMutation,
  useRejectReelMutation,
  useRegenerateReelMutation,
  useGetReleaseKitsQuery,
  useGetWorkSummaryQuery,
  useGetReleaseKitQuery,
  useCreateKitAssetsMutation,
  usePreviewRemakeKitAssetsMutation,
  useRemakeKitAssetsMutation,
  useGetCoverRedoQuery,
  usePreviewCoverRedoMutation,
  useStartCoverRedoMutation,
  useGetDateRedoQuery,
  usePreviewDateRedoMutation,
  useStartDateRedoMutation,
  useGetKitAssetVersionsQuery,
  useGetAssetShareQuery,
  useGetPublicAssetShareQuery,
  useGetAssetShareAccessQuery,
  useCreateAssetShareMutation,
  useDeleteAssetShareMutation,
  useGetAssetPublishesQuery,
  useGetAssetPublishDefaultsQuery,
  useCreateAssetPublishMutation,
  useUpdateAssetPublishMutation,
  useDeleteAssetPublishMutation,
  useGetSongPublishesQuery,
  useLazyGetSongPublishesQuery,
  useGetSongPublishDefaultsQuery,
  useCreateSongPublishMutation,
  useUpdateSongPublishMutation,
  useDeleteSongPublishMutation,
  useGetSpotifyCanvasQuery,
  useStartSpotifyCanvasMutation,
  useGetSpotifyCanvasSettingsQuery,
  useUpdateSpotifyCanvasSettingsMutation,
  useUpdateKitLookMutation,
  useGetReleaseKitLookQuery,
  useGetAccountTimeZoneQuery,
  useUpdateAccountTimeZoneMutation,
  useCreateKitZipMutation,
  useGetKitZipQuery,
  useGetCleanCopiesQuery,
  useGetKitProResQuery,
  useRequestKitProResMutation,
  useGetRolloutQuery,
  useAddRolloutDraftsMutation,
  useGetReleasesQuery,
  useGetReleaseOverviewQuery,
  useMarkReleaseViewedMutation,
  useGetReleasePlanQuery,
  useSetPlanChannelsMutation,
  useSetPlanStepChannelsMutation,
  useSkipPlanStepMutation,
  useUnskipPlanStepMutation,
  useSwapPlanStepMediaMutation,
  useMakePlanStepReelMutation,
  usePreviewReelsPlanQuery,
  useMakeReelsPlanMutation,
  useEndReelsPlanMutation,
  useGetVideoCreditsQuery,
  useGetCoverEditorQuery,
  useCreateCoverDesignMutation,
  useUpdateCoverDesignMutation,
  useDeleteCoverDesignMutation,
  useCreateCoverAiRunMutation,
  useApplyCoverDesignMutation,
  useGetCoverBackgroundsQuery,
  useCreateCoverMakerLeadMutation,
  useGetCoverMakerLeadQuery,
  useGetArtistProfileQuery,
  useMakeProfileAssetsMutation,
  useUseReleaseForProfileMutation,
  useGetProfileAssetVersionsQuery,
  useSetProfileAssetCurrentMutation,
  useMarkProfileAssetMutation,
  useSaveArtistBioMutation,
  useDraftArtistBioMutation,
  useCreateProfileZipMutation,
  useUploadProfileToSpotifyMutation,
  useUploadBioToSpotifyMutation,
  useGetProfileSendOverviewQuery,
  useGetProfileSendQuery,
  useStartProfileSendMutation,
  useSaveProfileSendSettingsMutation,
} = fymAPI;
